export const EventCategories = {
  RECORDING: 'recording',
  TRANSCRIPTION: 'transcription',
  NOTE: 'note',
  TEMPLATE: 'template',
  PATIENT: 'patient',
  BILLING: 'billing',
  APPOINTMENT: 'appointment',
  FORM: 'form',
  PERFORMANCE: 'performance',
  ERROR: 'error',
  ONBOARDING: 'onboarding',
  USER: 'user',
  ANALYTICS: 'analytics',
  ROLLOUT: 'rollout',
  ENTITLEMENT: 'entitlement',
  SUBSCRIPTION: 'subscription',
  PATIENT_DEDUPLICATION: 'patient_deduplication',
  PATIENT_IMPORT: 'patient_import',
  INSURANCE_CARD: 'insurance_card',
  JOTRX: 'jotrx',
  AUDIO_DEVICE: 'audio_device',
  EHR_EXTENSION: 'ehr_extension',
  ORG_SCHEDULE: 'org_schedule',
  PATIENT_CONTEXT_BAR: 'patient_context_bar',
  CLINIC_DOCUMENTS: 'clinic_documents',
  TELEHEALTH: 'telehealth',
  MEMBERSHIP: 'membership',
  TOUR: 'tour',
  ADDON_UPSELL: 'addon_upsell',
  ADDON_ONBOARDING: 'addon_onboarding',
  NOTE_VIEWER: 'note_viewer',
  SERVER_UI: 'server_ui',
} as const;

// Recording pipeline feature gates (ADR-003 through ADR-006).
// Each gate enables an independent service in the recording decomposition.
export const DynamicConfigs = {
  EHR_EXTENSION: 'ehr_extension_config',
  EHR_EXTENSION_URLS: 'ehr_extension_urls',
} as const;

export const FeatureGates = {
  AUDIO_DEVICE_SERVICE: 'feature_audio_device_service',
  ELIGIBILITY_CHECKS: 'feature_eligibility_checks',
  STORAGE_SERVICE: 'feature_storage_service',
  UPLOAD_SERVICE: 'feature_upload_service',
  RECORDING_SERVICE: 'feature_recording_service',
  SESSION_COMPLETION_SERVICE: 'feature_session_completion_service',
  EHR_EXTENSION_ADDON: 'ehr_extension_addon',
  PATIENT_CHAT: 'patient_chat',
  SERVER_DRIVEN_PATIENT_PAGE: 'feature_server_driven_patient_page',
  CALENDAR_V2: 'feature_calendar_v2',
  PATIENT_PAGE_V3_TEST_2026_05: 'patient_page_v3_test_2026_05',
  // GDN-2351: unified medications card on the V1 patient page (reads the
  // unified-history endpoint; replaces MedicationsChartBlock when on).
  UNIFIED_MEDICATIONS_CARD: 'feature_unified_medications_card',
  // GDN-2472: gate SHOWING AI chart suggestions on the unified cards. Polarity
  // is deliberate — a missing/off gate HIDES suggestions (providers don't trust
  // the modules yet; Jackson's call to filter by default). Turn on per-org to
  // bring the Suggested sections back.
  SHOW_CHART_SUGGESTIONS: 'feature_show_chart_suggestions',
  ASSESSMENT_TRAJECTORY_GHOST: 'feature_assessment_trajectory_ghost',
  DOSESPOT_SYNC: 'dosespot_sync',
  TELEHEALTH: 'feature_telehealth',
  JOTBILL_ANALYTICS: 'feature_jotbill_analytics',
  APPOINTMENT_LABELS: 'feature_appointment_labels',
  // GDN-2187: gates the self-serve scheduling settings surface (tab, nav entry,
  // and route). Default-off until rollout; flip on in Statsig to release.
  PATIENT_SELF_SCHEDULING: 'patient_self-scheduling',
  PRACTICE_METRICS: 'feature_practice_metrics',
  // GDN-2295: gates the practice cleanup wizard entry on the appointment
  // types page. Default-off; fixture-backed shell until GDN-2296 wires the
  // live bootstrap read.
  PRACTICE_CLEANUP_WIZARD: 'feature_practice_cleanup_wizard',
} as const;

export const EventNames = {
  // JotBill Revenue Analytics
  JOTBILL_ANALYTICS_PAGE_VIEWED: 'jotbill_analytics_page_viewed',
  JOTBILL_ANALYTICS_FILTER_CHANGED: 'jotbill_analytics_filter_changed',
  JOTBILL_ANALYTICS_WIDGET_RETRY: 'jotbill_analytics_widget_retry',
  // Practice Metrics (semantic layer) — attention → intent funnel
  PRACTICE_METRICS_HOME_SECTION_SHOWN: 'practice_metrics_home_section_shown',
  PRACTICE_METRICS_TILE_SHOWN: 'practice_metrics_tile_shown',
  PRACTICE_METRICS_DASHBOARD_OPENED: 'practice_metrics_dashboard_opened',
  PRACTICE_METRICS_DASHBOARD_EXITED: 'practice_metrics_dashboard_exited',
  PRACTICE_METRICS_TILE_HOVERED: 'practice_metrics_tile_hovered',
  PRACTICE_METRICS_TILE_CLICKED: 'practice_metrics_tile_clicked',
  PRACTICE_METRICS_DETAIL_OPENED: 'practice_metrics_detail_opened',
  PRACTICE_METRICS_IMPROVE_CLICKED: 'practice_metrics_improve_clicked',
  PRACTICE_METRICS_OPTIMIZATION_BOOKING_CLICKED:
    'practice_metrics_optimization_booking_clicked',
  PRACTICE_METRICS_OPTIMIZATION_CLOSED: 'practice_metrics_optimization_closed',
  // Practice cleanup wizard (GDN-2295) — open → steps → apply funnel.
  // Suffixes chosen from the Metabase allowlist: "completed"/"confirmed"
  // variants would be silently dropped by the pipeline.
  CLEANUP_WIZARD_OPENED: 'cleanup_wizard_opened',
  CLEANUP_WIZARD_STEP_SUBMITTED: 'cleanup_wizard_step_submitted',
  CLEANUP_WIZARD_APPLY_CLICKED: 'cleanup_wizard_apply_clicked',
  CLEANUP_WIZARD_CLOSED: 'cleanup_wizard_closed',
  // Recording events
  RECORDING_STARTED: 'recording_started',
  RECORDING_STOPPED: 'recording_stopped',
  RECORDING_PAUSED: 'recording_paused',
  RECORDING_RESUMED: 'recording_resumed',
  RECORDING_ERROR: 'recording_error',
  RECORDING_UPLOADED: 'recording_uploaded',
  RECORDING_START_FAILED: 'recording_start_failed',
  RECORDING_SHORT_DISMISSED: 'recording_short_dismissed',
  RECORDING_NO_AUDIO_DETECTED: 'recording_no_audio_detected',

  // Recording observability events (stuck-state detection)
  RECORDING_STUCK_STATE_DETECTED: 'recording_stuck_state_detected',
  RECORDING_READY_TO_RECORD_HACK_TRIGGERED: 'recording_ready_to_record_hack_triggered',
  RECORDING_STARTED_SUCCESSFULLY: 'recording_started_successfully',
  RECORDING_CLEANUP_COMPLETED: 'recording_cleanup_completed',
  RECORDING_STALE_STATE_AUTO_RECOVERED: 'recording_stale_state_auto_recovered',

  // Note lifecycle
  NOTE_CREATED: 'note_created',
  NOTE_VIEWED: 'note_viewed',
  NOTE_EDITED: 'note_edited',
  NOTE_SIGNED: 'note_signed',
  NOTE_DELETED: 'note_deleted',
  NOTE_EXPORTED: 'note_exported',
  NOTE_EMAILED: 'note_emailed',
  NOTE_COPIED: 'note_copied',
  NOTE_COSIGN_DISMISS_CLICKED: 'note_cosign_dismiss_clicked',
  NOTE_COSIGN_SNOOZE_CLICKED: 'note_cosign_snooze_clicked',
  NOTE_COSIGN_RESTORE_CLICKED: 'note_cosign_restore_clicked',
  NOTE_COSIGN_SHOW_DISMISSED_TOGGLED: 'note_cosign_show_dismissed_toggled',
  NOTE_AI_GENERATION_STARTED: 'note_ai_generation_started',
  NOTE_AI_GENERATION_COMPLETED: 'note_ai_generation_completed',

  // Note section interactions
  NOTE_SECTION_EDIT_SAVED: 'note_section_edit_saved',
  NOTE_SECTION_EDIT_CANCELLED: 'note_section_edit_cancelled',
  NOTE_SECTION_REGENERATED: 'note_section_regenerated',
  NOTE_SECTION_AI_EDIT_REQUESTED: 'note_section_ai_edit_requested',
  NOTE_SECTION_REVERTED: 'note_section_reverted',
  NOTE_SECTION_COPIED: 'note_section_copied',
  NOTE_SECTION_ADDED: 'note_section_added',
  NOTE_SECTION_REMOVED: 'note_section_removed',
  NOTE_SECTION_REORDERED: 'note_section_reordered',
  NOTE_SECTION_ATTESTATION_VIEWED: 'note_section_attestation_viewed',
  NOTE_SECTION_ATTESTATION_ACCEPTED: 'note_section_attestation_accepted',
  NOTE_SECTION_ATTESTATION_REJECTED: 'note_section_attestation_rejected',
  NOTE_TEMPLATE_EDIT_CLICKED: 'note_template_edit_clicked',
  NOTE_TEMPLATE_ADD_SECTION_CLICKED: 'note_template_add_section_clicked',
  NOTE_TEMPLATE_SECTION_DRAG_STARTED: 'note_template_section_drag_started',
  NOTE_TEMPLATE_REMOVE_SECTION_CLICKED: 'note_template_remove_section_clicked',

  // Note section editing interactions
  NOTE_SECTION_EDIT_CLICKED: 'note_section_edit_clicked',
  NOTE_SECTION_EDIT_DONE_CLICKED: 'note_section_edit_done_clicked',
  NOTE_SECTION_AI_EDIT_ACCEPT_CLICKED: 'note_section_ai_edit_accept_clicked',
  NOTE_SECTION_AI_EDIT_REJECT_CLICKED: 'note_section_ai_edit_reject_clicked',
  NOTE_SECTION_REVERT_CLICKED: 'note_section_revert_clicked',
  NOTE_SECTION_COPY_CLICKED: 'note_section_copy_clicked',
  NOTE_SECTION_SELECTOR_CLICKED: 'note_section_selector_clicked',
  NOTE_SECTION_DESELECT_ALL_CLICKED: 'note_section_deselect_all_clicked',

  // Note section AI interactions
  NOTE_SECTION_AI_MODE_SELECTED: 'note_section_ai_mode_selected',
  NOTE_SECTION_AI_INSTRUCTION_SUBMITTED: 'note_section_ai_instruction_submitted',
  NOTE_SECTION_AI_INSTRUCTION_RENDERED: 'note_section_ai_instruction_rendered',

  // Note view toggles
  NOTE_VIEW_TRANSCRIPT_CLICKED: 'note_view_transcript_clicked',
  NOTE_VIEW_NOTE_CLICKED: 'note_view_note_clicked',
  NOTE_TRANSCRIPT_TEXT_COPIED: 'note_transcript_text_copied',
  NOTE_FIND_REPLACE_OPENED: 'note_find_replace_opened',
  NOTE_FIND_REPLACE_REPLACE_CLICKED: 'note_find_replace_replace_clicked',
  NOTE_FIND_REPLACE_CANCELLED: 'note_find_replace_cancelled',

  // Note signing flow (Phase 3)
  NOTE_REVIEW_SIGN_CLICKED: 'note_review_sign_clicked',
  NOTE_PREVIEW_ENTERED: 'note_preview_entered',
  NOTE_COMPLETE_CLICKED: 'note_complete_clicked',
  NOTE_AMEND_CLICKED: 'note_amend_clicked',
  NOTE_BACK_TO_EDIT_CLICKED: 'note_back_to_edit_clicked',
  NOTE_COMPLETION_FAILED: 'note_completion_failed',

  // Note navigation/tabs (Phase 4)
  NOTE_TABS_TRANSCRIPT_CLICKED: 'note_tabs_transcript_clicked',
  NOTE_TABS_NOTE_CLICKED: 'note_tabs_note_clicked',
  NOTE_TABS_JOTDOC_CLICKED: 'note_tabs_jotdoc_clicked',

  // Note approval banner (Phase 6)
  NOTE_APPROVAL_BANNER_SHOWN: 'note_approval_banner_shown',
  NOTE_APPROVAL_NAVIGATION_CLICKED: 'note_approval_navigation_clicked',

  // Note chat interface (Phase 2)
  NOTE_CHAT_MESSAGE_SENT: 'note_chat_message_sent',
  NOTE_CHAT_RESPONSE_RECEIVED: 'note_chat_response_received',
  NOTE_CHAT_SUGGESTED_ACTION_CLICKED: 'note_chat_suggested_action_clicked',
  NOTE_CHAT_INPUT_FOCUSED: 'note_chat_input_focused',
  NOTE_CHAT_ERROR_OCCURRED: 'note_chat_error_occurred',
  NOTE_CHAT_SESSION_STARTED: 'note_chat_session_started',
  NOTE_CHAT_THREAD_CLEARED: 'note_chat_thread_cleared',
  NOTE_CHAT_SSE_PERFORMANCE: 'note_chat_sse_performance',
  NOTE_AI_CHAT_ONBOARDING_SHOWN: 'note_ai_chat_onboarding_shown',
  NOTE_AI_CHAT_ONBOARDING_DISMISSED: 'note_ai_chat_onboarding_dismissed',

  // Template switching workflow
  NOTE_TEMPLATE_SWITCH_CLICKED: 'note_template_switch_clicked',
  NOTE_TEMPLATE_SWITCH_MODAL_OPENED: 'note_template_switch_modal_opened',
  NOTE_TEMPLATE_SELECTED: 'note_template_selected',
  NOTE_TEMPLATE_REGENERATE_CLICKED: 'note_template_regenerate_clicked',
  NOTE_TEMPLATE_DROPDOWN_OPENED: 'note_template_dropdown_opened',
  NOTE_TEMPLATE_REGENERATE_CONFIRMED: 'note_template_regenerate_confirmed',
  NOTE_TEMPLATE_REGENERATE_CANCELLED: 'note_template_regenerate_cancelled',

  // NoteTopBar menu interactions (consolidated with view_mode metadata)
  NOTE_TOPBAR_MENU_OPENED: 'note_topbar_menu_opened',
  NOTE_TOPBAR_DELETE_CLICKED: 'note_topbar_delete_clicked',
  NOTE_TOPBAR_COPY_PASTE_SETTINGS_CLICKED: 'note_topbar_copy_paste_settings_clicked',
  NOTE_TOPBAR_FIND_REPLACE_CLICKED: 'note_topbar_find_replace_clicked',
  NOTE_TOPBAR_CHAT_TOGGLE_CLICKED: 'note_topbar_chat_toggle_clicked',
  NOTE_TOPBAR_DOWNLOAD_SIGNED_PDF_CLICKED: 'note_topbar_download_signed_pdf_clicked',

  // Note metadata patient assignment events
  NOTE_PATIENT_ASSIGNMENT_CLICKED: 'note_patient_assignment_clicked',
  NOTE_PATIENT_ASSIGNMENT_DIALOG_OPENED: 'note_patient_assignment_dialog_opened',
  NOTE_PATIENT_ASSIGNED: 'note_patient_assigned',
  NOTE_PATIENT_UNASSIGN_CLICKED: 'note_patient_unassign_clicked',
  NOTE_PATIENT_UNASSIGNED: 'note_patient_unassigned',
  NOTE_PATIENT_NAME_CLICKED: 'note_patient_name_clicked',
  NOTE_PATIENT_ASSIGNMENT_CANCELLED: 'note_patient_assignment_cancelled',
  NOTE_PAYER_CHIP_CLICKED: 'note_payer_chip_clicked',

  // Note title editing events
  NOTE_TITLE_EDIT_STARTED: 'note_title_edit_started',
  NOTE_TITLE_EDIT_CANCELLED: 'note_title_edit_cancelled',

  // Note metadata encounter editing events
  NOTE_ENCOUNTER_TIME_EDITOR_OPENED: 'note_encounter_time_editor_opened',
  NOTE_ENCOUNTER_TIME_SAVED: 'note_encounter_time_saved',
  NOTE_ENCOUNTER_CPT_EDITOR_OPENED: 'note_encounter_cpt_editor_opened',
  NOTE_ENCOUNTER_CPT_CODES_SAVED: 'note_encounter_cpt_codes_saved',
  NOTE_ENCOUNTER_ICD10_EDITOR_OPENED: 'note_encounter_icd10_editor_opened',
  NOTE_ENCOUNTER_ICD10_CODES_SAVED: 'note_encounter_icd10_codes_saved',

  // Note telemetry events (mirrored from NoteTelemetry system)
  NOTE_SELECTION_UPDATE: 'note_selection_update',
  NOTE_TEXT_SELECTED: 'note_text_selected',
  NOTE_TEXT_COPIED: 'note_text_copied',
  NOTE_NOTE_VIEWED: 'note_note_viewed',
  NOTE_SECTION_RENDERED: 'note_section_rendered',
  NOTE_SECTION_INTERACTED: 'note_section_interacted',
  NOTE_SECTION_EDITED: 'note_section_edited',

  // Template events
  TEMPLATE_SELECTED: 'template_selected',
  TEMPLATE_CUSTOMIZED: 'template_customized',
  TEMPLATE_SAVED: 'template_saved',
  TEMPLATE_DELETED: 'template_deleted',
  TEMPLATE_SHARED: 'template_shared',
  TEMPLATE_SECTION_ADDED: 'template_section_added',
  TEMPLATE_SECTION_REMOVED: 'template_section_removed',
  TEMPLATE_REORDERED: 'template_reordered',
  TEMPLATE_PINNED: 'template_pinned',
  TEMPLATE_UNPINNED: 'template_unpinned',

  // Add template to list banner (NoteChatView)
  TEMPLATE_ADD_TO_LIST_BANNER_SHOWN: 'template_add_to_list_banner_shown',
  TEMPLATE_ADD_TO_LIST_BANNER_DISMISSED: 'template_add_to_list_banner_dismissed',
  TEMPLATE_ADD_TO_LIST_CLICKED: 'template_add_to_list_clicked',

  // Template customization flow
  TEMPLATE_CUSTOMIZATION_STARTED: 'template_customization_started',
  TEMPLATE_SECTION_MODIFIED: 'template_section_modified',
  TEMPLATE_PREVIEW_GENERATED: 'template_preview_generated',
  TEMPLATE_SAVE_ATTEMPTED: 'template_save_attempted',

  // Template AI builder
  TEMPLATE_AI_CHAT_STARTED: 'template_ai_chat_started',
  TEMPLATE_AI_MESSAGE_SENT: 'template_ai_message_sent',
  TEMPLATE_AI_REGENERATION_COMPLETED: 'template_ai_regeneration_completed',

  // Module selector events
  TEMPLATE_MODULE_SELECTOR_OPENED: 'template_module_selector_opened',
  TEMPLATE_MODULE_SELECTOR_CLOSED: 'template_module_selector_closed',
  TEMPLATE_MODULE_SELECTOR_SEARCH_PERFORMED: 'template_module_selector_search_performed',
  TEMPLATE_MODULE_SELECTOR_FILTER_CHANGED: 'template_module_selector_filter_changed',
  TEMPLATE_MODULE_SELECTOR_SECTION_SELECTED: 'template_module_selector_section_selected',
  TEMPLATE_MODULE_SELECTOR_SECTION_DESELECTED: 'template_module_selector_section_deselected',
  TEMPLATE_MODULE_SELECTOR_PREVIEW_TOGGLED: 'template_module_selector_preview_toggled',
  TEMPLATE_MODULE_SELECTOR_CUSTOM_SECTION_INITIATED:
    'template_module_selector_custom_section_initiated',
  TEMPLATE_MODULE_SELECTOR_CUSTOM_SECTION_CANCELLED:
    'template_module_selector_custom_section_cancelled',
  TEMPLATE_MODULE_SELECTOR_SELECTION_CLEARED: 'template_module_selector_selection_cleared',
  TEMPLATE_MODULE_SELECTOR_SECTIONS_ADDED: 'template_module_selector_sections_added',
  TEMPLATE_MODULE_SELECTOR_ABANDONED: 'template_module_selector_abandoned',

  // Module conversation events (AI-powered section customization)
  MODULE_CONVERSATION_OPENED: 'module_conversation_opened',
  MODULE_CONVERSATION_NOTE_SELECTED: 'module_conversation_note_selected',
  MODULE_CONVERSATION_FEEDBACK_SUBMITTED: 'module_conversation_feedback_submitted',
  MODULE_CONVERSATION_ACCEPT_CLICKED: 'module_conversation_accept_clicked',
  MODULE_CONVERSATION_EXIT_CLICKED: 'module_conversation_exit_clicked',
  MODULE_CONVERSATION_CLOSED: 'module_conversation_closed',
  MODULE_CONVERSATION_STARTED: 'module_conversation_started',
  MODULE_CONVERSATION_ITERATION_COMPLETED: 'module_conversation_iteration_completed',
  MODULE_CONVERSATION_ERROR_OCCURRED: 'module_conversation_error_occurred',

  // Patient events
  PATIENT_CREATION_STARTED: 'patient_creation_started',
  PATIENT_CREATION_ABANDONED: 'patient_creation_abandoned',
  PATIENT_CREATED: 'patient_created',
  PATIENT_UPDATED: 'patient_updated',
  PATIENT_SELECTED: 'patient_selected',
  PATIENT_LIST_VIEWED: 'patient_list_viewed',
  PATIENT_DETAIL_ACCESSED: 'patient_detail_accessed',
  PATIENT_DELETED: 'patient_deleted',

  // Medical records export events (patient record packets)
  MEDICAL_RECORDS_EXPORT_OPENED: 'medical_records_export_opened',
  MEDICAL_RECORDS_EXPORT_REVIEW_OPENED: 'medical_records_export_review_opened',
  MEDICAL_RECORDS_EXPORT_OUTPUT_MODE_CHANGED: 'medical_records_export_output_mode_changed',
  MEDICAL_RECORDS_EXPORT_GENERATION_STARTED: 'medical_records_export_generation_started',
  MEDICAL_RECORDS_EXPORT_DOWNLOAD_REQUESTED: 'medical_records_export_download_requested',

  // Patient assistant (AI chat) events
  PATIENT_ASSISTANT_OPENED: 'patient_assistant_opened',
  PATIENT_ASSISTANT_CLOSED: 'patient_assistant_closed',

  // Patient info modal events
  PATIENT_INFO_MODAL_OPENED: 'patient_info_modal_opened',
  PATIENT_INFO_MODAL_CLOSED: 'patient_info_modal_closed',
  PATIENT_INFO_FIELD_SAVED: 'patient_info_field_saved',
  PATIENT_INFO_SAVE_FAILED: 'patient_info_save_failed',
  PATIENT_INFO_CLINICAL_ITEM_ADDED: 'patient_info_clinical_item_added',
  PATIENT_INFO_CLINICAL_ITEM_UPDATED: 'patient_info_clinical_item_updated',
  PATIENT_INFO_CLINICAL_ITEM_REMOVED: 'patient_info_clinical_item_removed',

  PATIENT_DOCUMENT_VIEWED: 'patient_document_viewed',
  PATIENT_DOCUMENT_UPLOADED: 'patient_document_uploaded',
  PATIENT_DOCUMENT_CREATED: 'patient_document_created',
  PATIENT_DOCUMENT_DELETED: 'patient_document_deleted',
  PATIENT_DOCUMENT_FILTERED: 'patient_document_filtered',
  PATIENT_DOCUMENT_BULK_ACTION: 'patient_document_bulk_action',

  // PDF & Document Operations
  PDF_LOAD_PERFORMANCE: 'pdf_load_performance',
  DOCUMENT_INTERACTION: 'document_interaction',
  DOCUMENT_PRINT_EXPORT: 'document_print_export',
  DOCUMENT_ERROR: 'document_error',
  DOCUMENT_LIST_LOADED: 'document_list_loaded',
  DOCUMENT_PAGINATION_CHANGED: 'document_pagination_changed',

  // Document Pages (new document viewer pages)
  DOCUMENTS_PAGE_VIEWED: 'documents_page_viewed',
  LABS_PAGE_VIEWED: 'labs_page_viewed',
  FORMS_PAGE_VIEWED: 'forms_page_viewed',
  SENT_DOCUMENTS_PAGE_VIEWED: 'sent_documents_page_viewed',
  SENT_DOCUMENTS_FILTER_CHANGED: 'sent_documents_filter_changed',
  DOCUMENTS_ROW_CLICKED: 'documents_row_clicked',
  DOCUMENTS_SEARCH_PERFORMED: 'documents_search_performed',
  DOCUMENTS_FILTER_APPLIED: 'documents_filter_applied',
  DOCUMENTS_SORT_CHANGED: 'documents_sort_changed',
  DOCUMENTS_MOBILE_VIEW_OPENED: 'documents_mobile_view_opened',
  DOCUMENTS_MOBILE_VIEW_CLOSED: 'documents_mobile_view_closed',
  DOCUMENT_DOWNLOADED: 'document_downloaded',
  DOCUMENT_TEMPLATE_LIBRARY_VIEWED: 'document_template_library_viewed',
  DOCUMENT_TEMPLATE_SELECTED: 'document_template_selected',
  DOCUMENT_TEMPLATE_DELETED: 'document_template_deleted',
  DOCUMENT_GENERATE_INITIATED: 'document_generate_initiated',

  // Billing events
  BILLING_CLAIM_CREATED: 'billing_claim_created',
  BILLING_CLAIM_VIEWED: 'billing_claim_viewed',
  BILLING_CLAIM_EDITED: 'billing_claim_edited',
  BILLING_CLAIM_DELETED: 'billing_claim_deleted',
  BILLING_CLAIM_SUBMITTED: 'billing_claim_submitted',
  BILLING_CLAIM_REBUILT: 'billing_claim_rebuilt',
  BILLING_CLAIM_AUTO_REBUILD_STARTED: 'billing_claim_auto_rebuild_started',
  BILLING_INSURANCE_LEARN_MORE_CLICKED: 'bill_insurance_learn_more_clicked',
  BILLING_INSURANCE_RULES_CLICKED: 'bill_insurance_rules_clicked',
  BILLING_PATIENT_BILLED_FROM_ERA: 'billing_patient_billed_from_era',
  BILLING_PATIENT_BILL_FAILED: 'billing_patient_bill_failed',
  BILLING_PATIENT_BILL_CLICKED: 'billing_patient_bill_clicked',
  BILLING_PATIENT_BILL_PAYMENT_URL_COPIED: 'billing_patient_bill_payment_url_copied',
  BILLING_MORNING_WORKLIST_FILTER_CHANGED: 'billing_morning_worklist_filter_changed',
  BILLING_MORNING_WORKLIST_ACCOUNT_CLICKED: 'billing_morning_worklist_account_clicked',
  BILLING_MORNING_WORKLIST_CLASSIC_DASHBOARD_CLICKED:
    'billing_morning_worklist_classic_dashboard_clicked',

  // X12 Download events
  BILLING_ERA_DOWNLOADED: 'billing_era_downloaded',
  BILLING_ERA_DOWNLOAD_FAILED: 'billing_era_download_failed',
  BILLING_ERA_BULK_DOWNLOADED: 'billing_era_bulk_downloaded',
  BILLING_ERA_BULK_DOWNLOAD_FAILED: 'billing_era_bulk_download_failed',
  BILLING_CLAIM_X12_DOWNLOADED: 'billing_claim_x12_downloaded',
  BILLING_CLAIM_X12_DOWNLOAD_FAILED: 'billing_claim_x12_download_failed',
  BILLING_CLAIM_X12_BULK_DOWNLOADED: 'billing_claim_x12_bulk_downloaded',
  BILLING_CLAIM_X12_BULK_DOWNLOAD_FAILED: 'billing_claim_x12_bulk_download_failed',

  // ERA triage attention events
  BILLING_CLAIM_ATTENTION_DISMISSED: 'billing_claim_attention_dismissed',
  BILLING_CLAIM_ATTENTION_SNOOZED: 'billing_claim_attention_snoozed',
  BILLING_CLAIM_ATTENTION_FAILED: 'billing_claim_attention_failed',

  // Analytics events
  REPORTS_ANALYTICS_INTERESTED_CLICKED: 'reports_analytics_interested_clicked',

  // Reports engine events
  REPORT_OPENED: 'report_opened',
  REPORT_RUN_SUBMITTED: 'report_run_submitted',
  REPORT_EXPORT_CLICKED: 'report_export_clicked',
  REPORT_VIEW_SAVE_SUBMITTED: 'report_view_save_submitted',
  REPORT_SAVED_VIEW_OPENED: 'report_saved_view_opened',
  REPORT_TOKEN_CHANGED: 'report_token_changed',
  REPORT_GROUP_TOGGLED: 'report_group_toggled',
  REPORT_LABEL_FILTER_CHANGED: 'report_label_filter_changed',

  // Command palette events
  COMMAND_PALETTE_OPENED: 'command_palette_opened',
  COMMAND_PALETTE_ACTION_SELECTED: 'command_palette_action_selected',

  // Communications events
  PATIENT_COMMUNICATIONS_INTERESTED_CLICKED: 'patient_communications_interested_clicked',

  // Audit events
  AUDIT_LEARN_MORE_CLICKED: 'audit_learn_more_clicked',
  AUDIT_SEND_DOCUMENTS_CLICKED: 'audit_send_documents_clicked',
  AUDIT_RULES_CLICKED: 'audit_rules_clicked',

  // Eligibility Checks events
  ELIGIBILITY_CHECKS_LEARN_MORE_CLICKED: 'eligibility_checks_learn_more_clicked',
  ELIGIBILITY_WARNING_VIEWED: 'eligibility_warning_viewed',
  ELIGIBILITY_WARNING_POPOVER_OPENED: 'eligibility_warning_popover_opened',
  ELIGIBILITY_WARNING_LEARN_MORE_CLICKED: 'eligibility_warning_learn_more_clicked',
  ELIGIBILITY_WARNING_DISMISSED: 'eligibility_warning_dismissed',
  ELIGIBILITY_BADGE_VIEWED: 'eligibility_badge_viewed',
  ELIGIBILITY_BADGE_POPOVER_OPENED: 'eligibility_badge_popover_opened',
  ELIGIBILITY_BADGE_RUN_CHECK_CLICKED: 'eligibility_badge_run_check_clicked',
  ELIGIBILITY_BADGE_VIEW_DETAILS_CLICKED: 'eligibility_badge_view_details_clicked',
  ELIGIBILITY_VERIFICATION_FORM_OPENED: 'eligibility_verification_form_opened',
  ELIGIBILITY_VERIFICATION_FORM_CLOSED: 'eligibility_verification_form_closed',
  ELIGIBILITY_VERIFICATION_SUBMITTED: 'eligibility_verification_submitted',
  ELIGIBILITY_VERIFICATION_HISTORY_OPENED: 'eligibility_verification_history_opened',
  ELIGIBILITY_ALERTS_SNOOZED_CLICKED: 'eligibility_alerts_snoozed_clicked',
  ELIGIBILITY_ALERTS_DISMISSED_CLICKED: 'eligibility_alerts_dismissed_clicked',
  ELIGIBILITY_ALERTS_RESUMED_CLICKED: 'eligibility_alerts_resumed_clicked',
  ELIGIBILITY_COLLECTION_FORM_OPENED: 'eligibility_collection_form_opened',
  ELIGIBILITY_COLLECTION_FORM_CLOSED: 'eligibility_collection_form_closed',
  ELIGIBILITY_COLLECTION_SUBMITTED: 'eligibility_collection_submitted',
  ELIGIBILITY_WORKLIST_OPENED: 'eligibility_worklist_opened',
  ELIGIBILITY_WORKLIST_ROW_CLICKED: 'eligibility_worklist_row_clicked',
  ELIGIBILITY_WORKLIST_DRAWER_OPENED: 'eligibility_worklist_drawer_opened',
  ELIGIBILITY_WORKLIST_SEGMENT_CHANGED: 'eligibility_worklist_segment_changed',
  ELIGIBILITY_WORKLIST_INVOICE_CLICKED: 'eligibility_worklist_invoice_clicked',

  // Note audit sidebar events (in-note JotAudit feature)
  NOTE_AUDIT_TAB_CLICKED: 'note_audit_tab_clicked',
  NOTE_AUDIT_SIDEBAR_OPENED: 'note_audit_sidebar_opened',
  NOTE_AUDIT_SIDEBAR_CLOSED: 'note_audit_sidebar_closed',
  NOTE_AUDIT_SIDEBAR_VIEWED: 'note_audit_sidebar_viewed',
  NOTE_SECTION_AUDIT_BADGE_CLICKED: 'note_section_audit_badge_clicked',
  NOTE_AUDIT_TAB_SWITCHED: 'note_audit_tab_switched',
  NOTE_AUDIT_RULE_CLICKED: 'note_audit_rule_clicked',
  NOTE_AUDIT_RULE_FIX_CLICKED: 'note_audit_rule_fix_clicked',
  NOTE_AUDIT_RULE_JUMP_TO_SECTION_CLICKED: 'note_audit_rule_jump_to_section_clicked',
  NOTE_AUDIT_RULE_INFO_VIEWED: 'note_audit_rule_info_viewed', // DEPRECATED: Use NOTE_AUDIT_RULE_JUMP_TO_SECTION_CLICKED
  NOTE_AUDIT_RULE_DISMISSED: 'note_audit_rule_dismissed',
  NOTE_AUDIT_RULE_MARKED_RESOLVED: 'note_audit_rule_marked_resolved',
  NOTE_AUDIT_RULE_UNDONE: 'note_audit_rule_undone',
  NOTE_AUDIT_RULE_ANALYTICS_CLICKED: 'note_audit_rule_analytics_clicked',
  NOTE_AUDIT_RULE_EDIT_CLICKED: 'note_audit_rule_edit_clicked',
  NOTE_AUDIT_RULE_DISABLED: 'note_audit_rule_disabled',
  NOTE_AUDIT_RULE_ENABLED: 'note_audit_rule_enabled',
  NOTE_AUDIT_SECTION_TOGGLED: 'note_audit_section_toggled',
  NOTE_AUDIT_RUN_CLICKED: 'note_audit_run_clicked',
  NOTE_AUDIT_REANALYZE_CLICKED: 'note_audit_reanalyze_clicked', // DEPRECATED: Use NOTE_AUDIT_RUN_CLICKED
  NOTE_AUDIT_RUN_COMPLETED: 'note_audit_run_completed',
  NOTE_AUDIT_REANALYZE_COMPLETED: 'note_audit_reanalyze_completed', // DEPRECATED: Use NOTE_AUDIT_RUN_COMPLETED
  NOTE_AUDIT_CPT_VIEWED: 'note_audit_cpt_viewed',
  NOTE_AUDIT_SUGGESTED_CODES_APPLIED: 'note_audit_suggested_codes_applied',
  NOTE_AUDIT_RULES_LINK_CLICKED: 'note_audit_rules_link_clicked',

  // CPT code sidebar interactions
  NOTE_AUDIT_CPT_DISPLAYED: 'note_audit_cpt_displayed',
  NOTE_AUDIT_CPT_APPROVED: 'note_audit_cpt_approved',
  NOTE_AUDIT_CPT_PICKER_OPENED: 'note_audit_cpt_picker_opened',
  NOTE_AUDIT_CPT_CHANGED: 'note_audit_cpt_changed',

  // Payer-aware template banner (JotAudit sidebar)
  NOTE_AUDIT_PAYER_AWARE_BANNER_SHOWN: 'note_audit_payer_aware_banner_shown',
  NOTE_AUDIT_PAYER_AWARE_BANNER_DISMISSED: 'note_audit_payer_aware_banner_dismissed',
  NOTE_AUDIT_PAYER_AWARE_REGENERATE_CLICKED: 'note_audit_payer_aware_regenerate_clicked',

  // JotAudit dashboard events (clinic admin audit center)
  // NEW - Use these going forward with dashboard_ prefix
  JOTAUDIT_DASHBOARD_PAGE_VIEWED: 'jotaudit_dashboard_page_viewed',
  JOTAUDIT_DASHBOARD_TAB_CHANGED: 'jotaudit_dashboard_tab_changed',
  JOTAUDIT_DASHBOARD_PROVIDER_CLICKED: 'jotaudit_dashboard_provider_clicked',
  JOTAUDIT_DASHBOARD_PROVIDER_DETAIL_VIEWED: 'jotaudit_dashboard_provider_detail_viewed',
  JOTAUDIT_DASHBOARD_NOTE_CLICKED: 'jotaudit_dashboard_note_clicked',
  JOTAUDIT_DASHBOARD_NOTE_DETAIL_VIEWED: 'jotaudit_dashboard_note_detail_viewed',
  JOTAUDIT_DASHBOARD_OPEN_NOTE_CLICKED: 'jotaudit_dashboard_open_note_clicked',
  JOTAUDIT_DASHBOARD_FILTER_CHANGED: 'jotaudit_dashboard_filter_changed',

  // DEPRECATED - Keep for backward compatibility, use JOTAUDIT_DASHBOARD_* instead
  JOTAUDIT_PAGE_VIEWED: 'jotaudit_page_viewed',
  JOTAUDIT_TAB_CHANGED: 'jotaudit_tab_changed',
  JOTAUDIT_PROVIDER_CLICKED: 'jotaudit_provider_clicked',
  JOTAUDIT_PROVIDER_DETAIL_VIEWED: 'jotaudit_provider_detail_viewed',
  JOTAUDIT_NOTE_CLICKED: 'jotaudit_note_clicked',
  JOTAUDIT_NOTE_DETAIL_VIEWED: 'jotaudit_note_detail_viewed',
  JOTAUDIT_OPEN_NOTE_CLICKED: 'jotaudit_open_note_clicked',
  JOTAUDIT_FILTER_CHANGED: 'jotaudit_filter_changed',

  // Dashboard: Providers Tab
  JOTAUDIT_DASHBOARD_PROVIDERS_FILTER_CHANGED: 'jotaudit_dashboard_providers_filter_changed',
  JOTAUDIT_DASHBOARD_PROVIDER_NOTES_FILTERED: 'jotaudit_dashboard_provider_notes_filtered',
  JOTAUDIT_DASHBOARD_PROVIDER_NOTES_PAGINATED: 'jotaudit_dashboard_provider_notes_paginated',
  JOTAUDIT_DASHBOARD_PROVIDER_SUBTAB_CHANGED: 'jotaudit_dashboard_provider_subtab_changed',

  // Dashboard: Rules Tab
  JOTAUDIT_DASHBOARD_RULES_FILTER_CHANGED: 'jotaudit_dashboard_rules_filter_changed',
  JOTAUDIT_DASHBOARD_COMPARISON_MODE_TOGGLED: 'jotaudit_dashboard_comparison_mode_toggled',
  JOTAUDIT_DASHBOARD_RULE_CLICKED: 'jotaudit_dashboard_rule_clicked',
  JOTAUDIT_DASHBOARD_ADD_RULE_CLICKED: 'jotaudit_dashboard_add_rule_clicked',
  JOTAUDIT_DASHBOARD_UPLOAD_RULES_CLICKED: 'jotaudit_dashboard_upload_rules_clicked',
  JOTAUDIT_DASHBOARD_STAT_CARD_CLICKED: 'jotaudit_dashboard_stat_card_clicked',

  // Dashboard: Rules Introduction Card
  JOTAUDIT_RULES_INTRO_VIEWED: 'jotaudit_rules_intro_viewed',
  JOTAUDIT_RULES_INTRO_TRY_IT_CLICKED: 'jotaudit_rules_intro_try_it_clicked',
  JOTAUDIT_RULES_INTRO_LEARN_MORE_CLICKED: 'jotaudit_rules_intro_learn_more_clicked',
  JOTAUDIT_RULES_INTRO_VIDEO_PLAYED: 'jotaudit_rules_intro_video_played',
  JOTAUDIT_RULES_INTRO_DISMISSED: 'jotaudit_rules_intro_dismissed',

  // Note Audit: Learn More Video Modal
  JOTAUDIT_LEARN_MORE_BUTTON_CLICKED: 'jotaudit_learn_more_button_clicked',
  JOTAUDIT_LEARN_MORE_VIDEO_CLOSED: 'jotaudit_learn_more_video_closed',

  // Dashboard: Rule Analytics Page
  JOTAUDIT_DASHBOARD_RULE_ANALYTICS_VIEWED: 'jotaudit_dashboard_rule_analytics_viewed',
  JOTAUDIT_DASHBOARD_RULE_CONTENT_EXPANDED: 'jotaudit_dashboard_rule_content_expanded',
  JOTAUDIT_DASHBOARD_VIEW_VIOLATIONS_CLICKED: 'jotaudit_dashboard_view_violations_clicked',
  JOTAUDIT_DASHBOARD_RULE_EDIT_CLICKED: 'jotaudit_dashboard_rule_edit_clicked',
  JOTAUDIT_DASHBOARD_RULE_DISABLED: 'jotaudit_dashboard_rule_disabled',
  JOTAUDIT_DASHBOARD_RULE_ENABLED: 'jotaudit_dashboard_rule_enabled',
  JOTAUDIT_DASHBOARD_ANALYTICS_PROVIDER_CLICKED: 'jotaudit_dashboard_analytics_provider_clicked',
  JOTAUDIT_DASHBOARD_ANALYTICS_FILTER_CHANGED: 'jotaudit_dashboard_analytics_filter_changed',

  // Dashboard: Notes Tab
  JOTAUDIT_DASHBOARD_NOTES_FILTER_CHANGED: 'jotaudit_dashboard_notes_filter_changed',
  JOTAUDIT_DASHBOARD_NOTES_PAGINATED: 'jotaudit_dashboard_notes_paginated',

  // Dashboard: Note Audit Detail Page
  JOTAUDIT_DASHBOARD_CPT_REVIEW_VIEWED: 'jotaudit_dashboard_cpt_review_viewed',
  JOTAUDIT_DASHBOARD_REVISION_HISTORY_VIEWED: 'jotaudit_dashboard_revision_history_viewed',
  JOTAUDIT_DASHBOARD_REVISION_SELECTED: 'jotaudit_dashboard_revision_selected',
  JOTAUDIT_DASHBOARD_RULE_COMPLIANCE_ROW_CLICKED: 'jotaudit_dashboard_rule_compliance_row_clicked',

  // Dashboard: Error & Retry
  JOTAUDIT_DASHBOARD_ERROR_DISPLAYED: 'jotaudit_dashboard_error_displayed',
  JOTAUDIT_DASHBOARD_RETRY_CLICKED: 'jotaudit_dashboard_retry_clicked',

  // Rules AI Chat events (Audit Center rules editing)
  RULES_CHAT_MESSAGE_SENT: 'rules_chat_message_sent',
  RULES_CHAT_RESPONSE_RECEIVED: 'rules_chat_response_received',
  RULES_CHAT_ERROR_OCCURRED: 'rules_chat_error_occurred',
  RULES_CHAT_SESSION_STARTED: 'rules_chat_session_started',
  RULES_CHAT_THREAD_CLEARED: 'rules_chat_thread_cleared',
  RULES_CHAT_SSE_PERFORMANCE: 'rules_chat_sse_performance',
  RULES_CHAT_INTERRUPT_SHOWN: 'rules_chat_interrupt_shown',
  RULES_CHAT_INTERRUPT_RESPONDED: 'rules_chat_interrupt_responded',
  RULES_CHAT_OPENED: 'rules_chat_opened',
  RULES_CHAT_CLOSED: 'rules_chat_closed',

  // Template Build events
  TEMPLATE_BUILD_STARTED: 'template_build_started',
  TEMPLATE_BUILD_STARTED_FROM_TEMPLATE: 'template_build_started_from_template',
  TEMPLATE_BUILD_UPLOAD_TYPE: 'template_build_upload_type',
  TEMPLATE_BUILD_CHAT_MESSAGE_SENT: 'template_build_chat_message_sent',
  TEMPLATE_BUILD_SECTION_SELECTED: 'template_build_section_selected',
  TEMPLATE_BUILD_COMPLETED: 'template_build_completed',
  TEMPLATE_BUILD_ABANDONED: 'template_build_abandoned',
  TEMPLATE_BUILD_ERROR: 'template_build_error',

  // Rule Build events
  RULE_BUILD_PAGE_VIEWED: 'rule_build_page_viewed',
  RULE_BUILD_TASK_CREATED: 'rule_build_task_created',
  RULE_BUILD_TASK_CREATED_FROM_DOCUMENT: 'rule_build_task_created_from_document',
  RULE_BUILD_TASK_CREATED_FROM_PROMPT: 'rule_build_task_created_from_prompt',
  RULE_BUILD_TASK_CREATED_FROM_RULE: 'rule_build_task_created_from_rule',
  RULE_BUILD_DRAFT_SELECTED: 'rule_build_draft_selected',
  RULE_BUILD_DRAFT_EDITED: 'rule_build_draft_edited',
  RULE_BUILD_DRAFT_SAVED: 'rule_build_draft_saved',
  RULE_BUILD_DRAFT_MARKED_READY: 'rule_build_draft_marked_ready',
  RULE_BUILD_DRAFT_MARKED_REJECTED: 'rule_build_draft_marked_rejected',
  RULE_BUILD_DRAFT_DELETED: 'rule_build_draft_deleted',
  RULE_BUILD_DRAFTS_SAVED: 'rule_build_drafts_saved',
  RULE_BUILD_CHAT_MESSAGE_SENT: 'rule_build_chat_message_sent',
  RULE_BUILD_CHAT_RESPONSE_RECEIVED: 'rule_build_chat_response_received',
  RULE_BUILD_CHAT_INTERRUPT_SHOWN: 'rule_build_chat_interrupt_shown',
  RULE_BUILD_CHAT_INTERRUPT_RESPONDED: 'rule_build_chat_interrupt_responded',
  RULE_BUILD_CHAT_ERROR: 'rule_build_chat_error',
  RULE_BUILD_GENERATION_STARTED: 'rule_build_generation_started',
  RULE_BUILD_GENERATION_COMPLETED: 'rule_build_generation_completed',
  RULE_BUILD_GENERATION_FAILED: 'rule_build_generation_failed',
  RULE_BUILD_GENERATION_TIMEOUT: 'rule_build_generation_timeout',
  RULE_BUILD_ERROR: 'rule_build_error',

  // Appointment events
  APPOINTMENT_MODAL_OPENED: 'appointment_modal_opened',
  APPOINTMENT_TYPE_PICKER_OPENED: 'appointment_type_picker_opened',
  APPOINTMENT_TYPE_DEFAULTS_PREVIEW_OPENED: 'appointment_type_defaults_preview_opened',
  APPOINTMENT_TYPE_SELECTED: 'appointment_type_selected',
  APPOINTMENT_TYPE_MODAL_OPENED: 'appointment_type_modal_opened',
  APPOINTMENT_TYPES_MANAGEMENT_MODAL_OPENED: 'appointment_types_management_modal_opened',
  APPOINTMENT_TYPE_CREATED: 'appointment_type_created',
  APPOINTMENT_TYPE_UPDATED: 'appointment_type_updated',
  APPOINTMENT_TYPE_COLOR_CHANGED: 'appointment_type_color_changed',
  APPOINTMENT_TYPE_DELETE_CLICKED: 'appointment_type_delete_clicked',
  APPOINTMENT_TYPE_DELETED: 'appointment_type_deleted',
  APPOINTMENT_COLLISION_MODAL_OPENED: 'appointment_collision_modal_opened',
  APPOINTMENT_COLLISION_UNSCHEDULED_SELECTED: 'appointment_collision_unscheduled_selected',
  APPOINTMENT_RECURRING_ENABLED: 'appointment_recurring_enabled',
  APPOINTMENT_CREATED: 'appointment_created',
  APPOINTMENT_CREATION_FAILED: 'appointment_creation_failed',
  APPOINTMENT_CREATION_ABANDONED: 'appointment_creation_abandoned',
  APPOINTMENT_UPDATED: 'appointment_updated',
  APPOINTMENT_CANCELLED: 'appointment_cancelled',
  APPOINTMENT_RESCHEDULED: 'appointment_rescheduled',
  APPOINTMENT_COMPLETED: 'appointment_completed',
  APPOINTMENT_REMINDER_SENT: 'appointment_reminder_sent',
  APPOINTMENT_NOTE_ADDED: 'appointment_note_added',
  APPOINTMENT_NOTE_UPDATED: 'appointment_note_updated',
  // Inline form/reminder rule editors (calendar v2)
  APPOINTMENT_RULE_TIMING_CHANGED: 'appointment_rule_timing_changed',
  APPOINTMENT_RULE_ADDITIONAL_SEND_CLICKED: 'appointment_rule_additional_send_clicked',
  APPOINTMENT_RULE_CONTACT_WARNING_CLICKED: 'appointment_rule_contact_warning_clicked',

  // Appointment UI interactions
  APPOINTMENT_CARD_EXPANDED: 'appointment_card_expanded',
  APPOINTMENT_CARD_COLLAPSED: 'appointment_card_collapsed',
  APPOINTMENT_ACTION_CLICKED: 'appointment_action_clicked',
  APPOINTMENT_LIST_VIEWED: 'appointment_list_viewed',
  APPOINTMENT_POPOVER_PATIENT_PHONE_CLICKED: 'appointment_popover_patient_phone_clicked',

  // Delete-with-recorded-session warning (GDN-2325)
  APPOINTMENT_DELETE_RECORDING_WARNING_OPENED: 'appointment_delete_recording_warning_opened',
  APPOINTMENT_DELETE_RECORDING_WARNING_CONFIRM_CLICKED:
    'appointment_delete_recording_warning_confirm_clicked',
  APPOINTMENT_DELETE_RECORDING_WARNING_CANCEL_CLICKED:
    'appointment_delete_recording_warning_cancel_clicked',

  // Appointment event quick actions (calendar modals)
  APPOINTMENT_EVENT_QUICK_ACTION_OPENED: 'appointment_event_quick_action_opened',
  APPOINTMENT_EVENT_QUICK_ACTION_CLICKED: 'appointment_event_quick_action_clicked',
  APPOINTMENT_EVENT_QUICK_ACTION_COMPLETED: 'appointment_event_quick_action_completed',

  // Blocked Time events
  BLOCKED_TIME_MODAL_OPENED: 'blocked_time_modal_opened',
  BLOCKED_TIME_MODE_SELECTED: 'blocked_time_mode_selected',
  BLOCKED_TIME_CREATE_SUBMITTED: 'blocked_time_create_submitted',
  BLOCKED_TIME_UPDATE_SUBMITTED: 'blocked_time_update_submitted',
  BLOCKED_TIME_DELETE_CLICKED: 'blocked_time_delete_clicked',
  BLOCKED_TIME_DRAG_SUBMITTED: 'blocked_time_drag_submitted',
  BLOCKED_TIME_RESIZE_SUBMITTED: 'blocked_time_resize_submitted',

  // Org Schedule events
  ORG_SCHEDULE_VIEW_TOGGLED: 'org_schedule_view_toggled',
  ORG_SCHEDULE_PROVIDER_FILTER_CHANGED: 'org_schedule_provider_filter_changed',
  ORG_SCHEDULE_PATIENT_FILTER_CHANGED: 'org_schedule_patient_filter_changed',
  ORG_SCHEDULE_PROVIDER_REASSIGNED: 'org_schedule_provider_reassigned',

  // Schedule density (capacity clusters) events
  SCHEDULE_CLUSTER_CARD_CLICKED: 'schedule_cluster_card_clicked',
  SCHEDULE_CLUSTER_APPOINTMENT_SELECTED: 'schedule_cluster_appointment_selected',
  SCHEDULE_CLUSTER_DENSITY_TOGGLED: 'schedule_cluster_density_toggled',

  // Google Calendar events
  GOOGLE_CALENDAR_CONNECT_INITIATED: 'google_calendar_connect_initiated',
  GOOGLE_CALENDAR_CONNECTED: 'google_calendar_connected',
  GOOGLE_CALENDAR_CONNECTION_FAILED: 'google_calendar_connection_failed',
  GOOGLE_CALENDAR_SYNC_COMPLETED: 'google_calendar_sync_completed',
  GOOGLE_CALENDAR_DISCONNECTED: 'google_calendar_disconnected',

  // Form events
  FORM_CREATED: 'form_created',
  FORM_CLONED: 'form_cloned',
  FORM_DELETED: 'form_deleted',
  FORM_PUBLISHED: 'form_published',
  FORM_DRAFT_DELETED: 'form_draft_deleted',
  FORM_SENT: 'form_sent',
  FORM_VIEWED: 'form_viewed',
  FORM_SUBMITTED: 'form_submitted',
  FORM_COMPLETED: 'form_completed',

  // Performance events
  PERFORMANCE_TIMING: 'performance_timing',

  // Error events
  ERROR_OCCURRED: 'error_occurred',
  ERROR_API: 'error_api',
  ERROR_RECORDING: 'error_recording',
  ERROR_AI_PROCESSING: 'error_ai_processing',
  ERROR_NETWORK: 'error_network',
  PIPELINE_INIT_FAILED: 'pipeline_init_failed',

  // Auth events
  AUTH_SIGNUP_STARTED: 'auth_signup_started',
  AUTH_SIGNUP_COMPLETED: 'auth_signup_completed',
  AUTH_LOGIN_COMPLETED: 'auth_login_completed',
  AUTH_LOGOUT: 'auth_logout',

  // Onboarding events
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_PROFILE_COMPLETED: 'onboarding_profile_completed',
  ONBOARDING_PREFERENCES_SET: 'onboarding_preferences_set',
  ONBOARDING_PATH_SELECTED: 'onboarding_path_selected',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_SKIPPED: 'onboarding_skipped',

  // User events
  USER_SETTINGS_UPDATED: 'user_settings_updated',
  USER_FEATURE_ENABLED: 'user_feature_enabled',
  USER_FEATURE_DISABLED: 'user_feature_disabled',

  // CSAT (Customer Satisfaction) events
  CSAT_MODAL_OPENED: 'csat_modal_opened',
  CSAT_RATING_SUBMITTED: 'csat_rating_submitted',
  CSAT_FOLLOW_UP_SUBMITTED: 'csat_follow_up_submitted',
  CSAT_DISMISSED: 'csat_dismissed',
  CSAT_REFERRAL_INVITE_SHOWN: 'csat_referral_invite_shown',
  CSAT_REFERRAL_INVITE_DECLINED: 'csat_referral_invite_declined',
  CSAT_REVIEW_SURFACE_CLICKED: 'csat_review_surface_clicked',

  // Search events (generic with metadata)
  SEARCH_INITIATED: 'search_initiated',
  SEARCH_PERFORMED: 'search_performed',
  SEARCH_RESULT_CLICKED: 'search_result_clicked',
  SEARCH_CLEARED: 'search_cleared',
  SEARCH_ABANDONED: 'search_abandoned',

  // Navigation events (generic with metadata)
  NAVIGATION_CLICKED: 'navigation_clicked',
  NAVIGATION_BACK: 'navigation_back',
  NAVIGATION_QUICK_ACTION: 'navigation_quick_action',

  // Stripe Connect events
  STRIPE_CONNECT_INITIATED: 'stripe_connect_initiated',
  STRIPE_CONNECT_COMPLETED: 'stripe_connect_completed',
  STRIPE_CONNECT_FAILED: 'stripe_connect_failed',
  STRIPE_DASHBOARD_OPENED: 'stripe_dashboard_opened',

  // Payment Request events
  PAYMENT_REQUEST_INITIATED: 'payment_request_initiated',
  PAYMENT_MODAL_OPENED: 'payment_modal_opened',
  PAYMENT_REQUEST_SENT: 'payment_request_sent',
  PAYMENT_REQUEST_FAILED: 'payment_request_failed',

  // Patient Payment Methods events
  PATIENT_PAYMENT_METHOD_PANEL_EXPANDED: 'patient_payment_method_panel_expanded',
  PATIENT_PAYMENT_METHOD_ADD_CLICKED: 'patient_payment_method_add_clicked',
  PATIENT_PAYMENT_METHOD_ADDED: 'patient_payment_method_added',
  PATIENT_PAYMENT_METHOD_ADD_FAILED: 'patient_payment_method_add_failed',
  PATIENT_PAYMENT_METHOD_DELETED: 'patient_payment_method_deleted',
  PATIENT_PAYMENT_METHOD_DELETE_FAILED: 'patient_payment_method_delete_failed',
  PATIENT_PAYMENT_METHOD_SET_DEFAULT: 'patient_payment_method_set_default',

  // Patient Payment Ingestion events (Phase 2B)
  BILLING_PAYMENT_RECORDED: 'billing_payment_recorded',
  BILLING_CHARGES_CAPTURED: 'billing_charges_captured',

  // Settle sheet — amount-first allocation (GDN-2400)
  SETTLE_ALLOCATION_PREVIEW_TOGGLED: 'settle_allocation_preview_toggled',
  SETTLE_COPAY_CHIP_CLICKED: 'settle_copay_chip_clicked',
  SETTLE_CREDIT_REMAINDER_SUBMITTED: 'settle_credit_remainder_submitted',
  // Settle sheet — copay declaration (GDN-2405)
  SETTLE_PAYMENT_INTENT_CHANGED: 'settle_payment_intent_changed',
  SETTLE_COPAY_DECLARED_SUBMITTED: 'settle_copay_declared_submitted',

  // Statements Queue events (Phase 3)
  STATEMENTS_QUEUE_VIEWED: 'statements_queue_viewed',
  STATEMENTS_QUEUE_PAGE_CHANGED: 'statements_queue_page_changed',
  STATEMENTS_HISTORY_VIEWED: 'statements_history_viewed',

  // Patient Invoices events
  PATIENT_INVOICE_PANEL_EXPANDED: 'patient_invoice_panel_expanded',
  PATIENT_INVOICE_VIEWED: 'patient_invoice_viewed',
  PATIENT_INVOICE_DOWNLOADED: 'patient_invoice_downloaded',

  // SMS Onboarding events
  SMS_ONBOARDING_INITIATED: 'sms_onboarding_initiated',
  SMS_ONBOARDING_COMPLETED: 'sms_onboarding_completed',

  // Phone Verification events (user signup)
  PHONE_VERIFICATION_REQUESTED: 'phone_verification_requested',
  PHONE_VERIFICATION_MODAL_OPENED: 'phone_verification_modal_opened',
  PHONE_VERIFICATION_SUBMITTED: 'phone_verification_submitted',
  PHONE_VERIFICATION_COMPLETED: 'phone_verification_completed',
  PHONE_VERIFICATION_FAILED: 'phone_verification_failed',
  PHONE_VERIFICATION_RESEND_CLICKED: 'phone_verification_resend_clicked',

  // Invoice events
  STRIPE_INVOICES_VIEWED: 'stripe_invoices_viewed',

  // Toolbar events
  TOOLBAR_SHOWN: 'toolbar_shown',
  TOOLBAR_ACTION_CLICKED: 'toolbar_action_clicked',
  TOOLBAR_VISIBILITY_TOGGLED: 'toolbar_visibility_toggled',
  // Toolbar payment intercept — copay routing (GDN-2405)
  TOOLBAR_COPAY_ROUTE_SELECTED: 'toolbar_copay_route_selected',
  TOOLBAR_CHARGE_ROUTE_SELECTED: 'toolbar_charge_route_selected',

  // Toolbar Notification events
  TOOLBAR_NOTIFICATION_SHOWN: 'toolbar_notification_shown',
  TOOLBAR_NOTIFICATION_ACKNOWLEDGED: 'toolbar_notification_acknowledged',
  TOOLBAR_NOTIFICATION_DISMISSED: 'toolbar_notification_dismissed',
  TOOLBAR_NOTIFICATION_AUTO_COMPLETED: 'toolbar_notification_auto_completed',

  // Lab Order events
  LAB_ORDER_MODAL_OPENED: 'lab_order_modal_opened',
  LAB_ORDER_SEARCH_PERFORMED: 'lab_order_search_performed',
  LAB_ORDER_TEST_SELECTED: 'lab_order_test_selected',
  LAB_ORDER_BUNDLE_SELECTED: 'lab_order_bundle_selected',
  LAB_ORDER_BUNDLE_SAVED: 'lab_order_bundle_saved',
  LAB_ORDER_AI_RECOMMENDATIONS_APPLIED: 'lab_order_ai_recommendations_applied',
  LAB_ORDER_REQUISITION_GENERATED: 'lab_order_requisition_generated',
  LAB_ORDER_SENT: 'lab_order_sent',
  LAB_ORDER_COMPLETED: 'lab_order_completed',
  LAB_ORDER_FAILED: 'lab_order_failed',

  // Dashboard events
  DASHBOARD_VIEWED: 'dashboard_viewed',
  DASHBOARD_ACTION_CLICKED: 'dashboard_action_clicked',
  DASHBOARD_NOTE_INTERACTED: 'dashboard_note_interacted',
  DASHBOARD_REFRESH_CLICKED: 'dashboard_refresh_clicked',
  DASHBOARD_ONBOARDING_TASK_CLICKED: 'dashboard_onboarding_task_clicked',

  // Encounter events
  ENCOUNTER_SAVE_FOR_LATER_CLICKED: 'encounter_save_for_later_clicked',

  // Feature toggle events
  TOGGLE_SWITCHED_TO_NEW: 'toggle_switched_to_new',
  TOGGLE_SWITCHED_TO_CLASSIC: 'toggle_switched_to_classic',
  TOGGLE_MODE_VIEWED: 'toggle_mode_viewed',
  TOGGLE_GRADUATED: 'toggle_graduated',
  TOGGLE_NUDGE_APPLIED: 'toggle_nudge_applied',

  // Encounter workflow events - Phase 1 (Critical)
  // Recording lifecycle
  ENCOUNTER_RECORDING_START_CLICKED: 'encounter_recording_start_clicked',
  ENCOUNTER_RECORDING_PAUSE_CLICKED: 'encounter_recording_pause_clicked',
  ENCOUNTER_RECORDING_RESUME_CLICKED: 'encounter_recording_resume_clicked',
  ENCOUNTER_RECORDING_STOP_CLICKED: 'encounter_recording_stop_clicked',

  // Microphone setup
  ENCOUNTER_MICROPHONE_MENU_OPENED: 'encounter_microphone_menu_opened',
  ENCOUNTER_MICROPHONE_CHANGED: 'encounter_microphone_changed',
  ENCOUNTER_MIC_TEST_STARTED: 'encounter_mic_test_started',
  ENCOUNTER_MIC_TEST_COMPLETED: 'encounter_mic_test_completed',
  ENCOUNTER_MIC_AUTO_ACQUIRED: 'encounter_mic_auto_acquired',
  ENCOUNTER_MIC_AUTO_FALLBACK: 'encounter_mic_auto_fallback',
  ENCOUNTER_HEADPHONES_MODE_TOGGLED: 'encounter_headphones_mode_toggled',

  // Cross-tab recording
  ENCOUNTER_RECORDING_TAKEOVER_PROMPTED: 'encounter_recording_takeover_prompted',
  ENCOUNTER_RECORDING_TAKEOVER_ACCEPTED: 'encounter_recording_takeover_accepted',
  ENCOUNTER_RECORDING_TAKEOVER_CANCELLED: 'encounter_recording_takeover_cancelled',

  // Patient selection
  ENCOUNTER_PATIENT_SELECTED: 'encounter_patient_selected',
  ENCOUNTER_PATIENT_REMOVED: 'encounter_patient_removed',
  ENCOUNTER_PATIENT_CLICKED: 'encounter_patient_clicked',

  // Session configuration
  ENCOUNTER_APPOINTMENT_TYPE_CHANGED: 'encounter_appointment_type_changed',
  ENCOUNTER_DURATION_EDITED: 'encounter_duration_edited',
  ENCOUNTER_TELEHEALTH_TOGGLED: 'encounter_telehealth_toggled',
  ENCOUNTER_CPT_CODE_EDITED: 'encounter_cpt_code_edited',
  ENCOUNTER_TEMPLATE_CHANGED: 'encounter_template_changed',
  ENCOUNTER_SESSION_CHIPS_MENU_OPENED: 'encounter_session_chips_menu_opened',

  // Billing popover
  ENCOUNTER_BILLING_POPOVER_OPENED: 'encounter_billing_popover_opened',
  ENCOUNTER_BILLING_POPOVER_SECTION_TOGGLED: 'encounter_billing_popover_section_toggled',
  ENCOUNTER_ATTESTATION_TOGGLED: 'encounter_attestation_toggled',
  ENCOUNTER_BILLING_SIGN_NOTE_CLICKED: 'encounter_billing_sign_note_clicked',

  // Workflow completion
  ENCOUNTER_GENERATE_NOTE_CLICKED: 'encounter_generate_note_clicked',
  ENCOUNTER_GENERATE_NOTE_COMPLETED: 'encounter_generate_note_completed',
  ENCOUNTER_GENERATE_NOTE_FAILED: 'encounter_generate_note_failed',

  // Dictation management - Phase 2
  ENCOUNTER_DICTATION_CLICKED: 'encounter_dictation_clicked',
  ENCOUNTER_ADD_DICTATION_CLICKED: 'encounter_add_dictation_clicked',
  ENCOUNTER_DICTATION_DELETE_CLICKED: 'encounter_dictation_delete_clicked',
  ENCOUNTER_DICTATION_DELETED: 'encounter_dictation_deleted',
  ENCOUNTER_DICTATION_DELETE_CANCELLED: 'encounter_dictation_delete_cancelled',

  // Delete all transcripts - permanently removes all transcription data
  ENCOUNTER_DELETE_ALL_TRANSCRIPTS_CLICKED: 'encounter_delete_all_transcripts_clicked',
  ENCOUNTER_DELETE_ALL_TRANSCRIPTS_CONFIRMED: 'encounter_delete_all_transcripts_confirmed',
  ENCOUNTER_DELETE_ALL_TRANSCRIPTS_CANCELLED: 'encounter_delete_all_transcripts_cancelled',
  ENCOUNTER_DELETE_ALL_TRANSCRIPTS_COMPLETED: 'encounter_delete_all_transcripts_completed',
  ENCOUNTER_DELETE_ALL_TRANSCRIPTS_FAILED: 'encounter_delete_all_transcripts_failed',

  // Delete-encounter-with-recording warning (GDN-2325)
  ENCOUNTER_DELETE_RECORDING_WARNING_OPENED: 'encounter_delete_recording_warning_opened',
  ENCOUNTER_DELETE_RECORDING_WARNING_CONFIRM_CLICKED:
    'encounter_delete_recording_warning_confirm_clicked',
  ENCOUNTER_DELETE_RECORDING_WARNING_CANCEL_CLICKED:
    'encounter_delete_recording_warning_cancel_clicked',

  // Document editor - Phase 2
  ENCOUNTER_DOCUMENT_EDITOR_OPENED: 'encounter_document_editor_opened',
  ENCOUNTER_DOCUMENT_EDITOR_CLOSED: 'encounter_document_editor_closed',
  ENCOUNTER_DOCUMENT_TITLE_EDITED: 'encounter_document_title_edited',
  ENCOUNTER_DOCUMENT_CONTENT_EDITED: 'encounter_document_content_edited',
  ENCOUNTER_DOCUMENT_DELETED_FROM_EDITOR: 'encounter_document_deleted_from_editor',

  // Notepad - Phase 3
  ENCOUNTER_NOTEPAD_FOCUSED: 'encounter_notepad_focused',
  ENCOUNTER_NOTEPAD_CONTENT_ADDED: 'encounter_notepad_content_added',
  ENCOUNTER_NOTEPAD_EXPANDED: 'encounter_notepad_expanded',
  ENCOUNTER_NOTEPAD_COLLAPSED: 'encounter_notepad_collapsed',

  // Patient info tabs - Phase 3
  ENCOUNTER_PATIENT_INFO_TAB_CHANGED: 'encounter_patient_info_tab_changed',
  ENCOUNTER_PATIENT_DEMOGRAPHICS_VIEWED: 'encounter_patient_demographics_viewed',
  ENCOUNTER_LAST_ENCOUNTER_VIEWED: 'encounter_last_encounter_viewed',

  // Memos tab — session-reminders empty-state ad
  MEMOS_REMINDER_AD_SHOWN: 'memos_reminder_ad_shown',
  MEMOS_REMINDER_AD_DISMISSED: 'memos_reminder_ad_dismissed',

  // View & Navigation
  ENCOUNTER_VIEW_LOADED: 'encounter_view_loaded',
  ENCOUNTER_REDIRECTED_TO_NOTE: 'encounter_redirected_to_note',
  ENCOUNTER_ACTIVE_ROUTE_CREATED: 'encounter_active_route_created',

  // Error states
  ENCOUNTER_NOT_FOUND: 'encounter_not_found',
  ENCOUNTER_INITIALIZATION_ERROR: 'encounter_initialization_error',
  ENCOUNTER_AUDIO_PERMISSION_DENIED: 'encounter_audio_permission_denied',
  ENCOUNTER_REMOTE_RECORDING_DETECTED: 'encounter_remote_recording_detected',

  // Note title (when patient bar dismissed)
  ENCOUNTER_NOTE_TITLE_SAVED: 'encounter_note_title_saved',
  ENCOUNTER_NOTE_TITLE_CLEARED: 'encounter_note_title_cleared',

  // Additional session configuration
  ENCOUNTER_ICD10_CODES_EDITED: 'encounter_icd10_codes_edited',
  ENCOUNTER_PLACE_OF_SERVICE_EDITED: 'encounter_place_of_service_edited',
  ENCOUNTER_PATIENT_PICKER_OPENED: 'encounter_patient_picker_opened',
  ENCOUNTER_LAST_ENCOUNTER_NOTE_CLICKED: 'encounter_last_encounter_note_clicked',

  // Child component interactions
  ENCOUNTER_PIPELINE_RETRY_CLICKED: 'encounter_pipeline_retry_clicked',
  ENCOUNTER_IOS_RECORDING_TIP_DISMISSED: 'encounter_ios_recording_tip_dismissed',

  // Entitlement events
  ENTITLEMENT_UPGRADE_CLICKED: 'entitlement_upgrade_clicked',
  ENTITLEMENT_BLOCKED_OVERLAY_SHOWN: 'entitlement_blocked_overlay_shown',
  ENTITLEMENT_UPGRADE_REQUESTED: 'entitlement_upgrade_requested',
  ENTITLEMENT_BLOCKED_DISMISSED: 'entitlement_blocked_dismissed',

  // Subscription flow events
  // Flow-level
  SUBSCRIPTION_FLOW_STARTED: 'subscription_flow_started',
  SUBSCRIPTION_FLOW_STEP_COMPLETED: 'subscription_flow_step_completed',
  SUBSCRIPTION_FLOW_COMPLETED: 'subscription_flow_completed',
  SUBSCRIPTION_FLOW_ABANDONED: 'subscription_flow_abandoned',
  SUBSCRIPTION_STEP_NEXT_CLICKED: 'subscription_step_next_clicked',
  SUBSCRIPTION_STEP_BACK_CLICKED: 'subscription_step_back_clicked',

  // Tier selection
  SUBSCRIPTION_TIER_CARD_CLICKED: 'subscription_tier_card_clicked',
  SUBSCRIPTION_TIER_SELECTED: 'subscription_tier_selected',
  SUBSCRIPTION_TIER_CHANGED: 'subscription_tier_changed',
  SUBSCRIPTION_TIER_CONTINUE_CLICKED: 'subscription_tier_continue_clicked',

  // Add-on selection
  SUBSCRIPTION_ADDON_ADD_CLICKED: 'subscription_addon_add_clicked',
  SUBSCRIPTION_ADDON_ADDED: 'subscription_addon_added',
  SUBSCRIPTION_ADDON_REMOVE_CLICKED: 'subscription_addon_remove_clicked',
  SUBSCRIPTION_ADDON_REMOVED: 'subscription_addon_removed',
  SUBSCRIPTION_ADDON_LEARN_MORE_CLICKED: 'subscription_addon_learn_more_clicked',
  SUBSCRIPTION_CHANGE_TIER_CLICKED: 'subscription_change_tier_clicked',
  SUBSCRIPTION_ADDONS_CONTINUE_CLICKED: 'subscription_addons_continue_clicked',

  // Billing interval
  SUBSCRIPTION_BILLING_INTERVAL_TOGGLED: 'subscription_billing_interval_toggled',

  // Payment & completion
  SUBSCRIPTION_PAYMENT_STEP_VIEWED: 'subscription_payment_step_viewed',
  SUBSCRIPTION_PAYMENT_DETAILS_ENTERED: 'subscription_payment_details_entered',
  SUBSCRIPTION_PAYMENT_SUBMIT_CLICKED: 'subscription_payment_submit_clicked',
  SUBSCRIPTION_PAYMENT_SUCCEEDED: 'subscription_payment_succeeded',
  SUBSCRIPTION_PAYMENT_FAILED: 'subscription_payment_failed',
  SUBSCRIPTION_CREATED: 'subscription_created',

  // Subscription management page events
  SUBSCRIPTION_MANAGEMENT_PAGE_VIEWED: 'subscription_management_page_viewed',
  SUBSCRIPTION_BILLING_INTERVAL_PREVIEW: 'subscription_billing_interval_preview',
  SUBSCRIPTION_BILLING_INTERVAL_CONFIRMED: 'subscription_billing_interval_confirmed',
  SUBSCRIPTION_PAYMENT_METHOD_UPDATE_CLICKED: 'subscription_payment_method_update_clicked',
  SUBSCRIPTION_PAYMENT_METHOD_UPDATED: 'subscription_payment_method_updated',
  SUBSCRIPTION_CANCEL_CLICKED: 'subscription_cancel_clicked',
  SUBSCRIPTION_CANCEL_CONFIRMED: 'subscription_cancel_confirmed',
  SUBSCRIPTION_REACTIVATE_CLICKED: 'subscription_reactivate_clicked',
  SUBSCRIPTION_REACTIVATE_CONFIRMED: 'subscription_reactivate_confirmed',
  SUBSCRIPTION_DISCOUNT_DISPLAYED: 'subscription_discount_displayed',
  SUBSCRIPTION_TIER_COMPARISON_VIEWED: 'subscription_tier_comparison_viewed',
  SUBSCRIPTION_SOLO_INVITE_CTA_CLICKED: 'subscription_solo_invite_cta_clicked',
  SUBSCRIPTION_SOLO_PLAN_CHANGED: 'subscription_solo_plan_changed',
  SUBSCRIPTION_INVOICE_HISTORY_OPENED: 'subscription_invoice_history_opened',
  SUBSCRIPTION_INVOICE_HISTORY_CLOSED: 'subscription_invoice_history_closed',

  // Practice redesign (GDN-1618) — cross-cutting events introduced by the
  // Subscription + Members redesign and shared across multiple surfaces.
  PRACTICE_PERSON_CARD_OPENED: 'practice_person_card_opened',
  PRACTICE_PERSON_CARD_SECTION_EXPANDED: 'practice_person_card_section_expanded',
  PRACTICE_BATCH_EDIT_OPENED: 'practice_batch_edit_opened',
  PRACTICE_BATCH_EDIT_APPLIED: 'practice_batch_edit_applied',

  // Practice settings events
  ORGANIZATION_NAME_CHANGED: 'organization_name_changed',

  // Clinicians/organization member management events
  CLINICIANS_PAGE_VIEWED: 'clinicians_page_viewed',
  CLINICIANS_INVITE_DIALOG_OPENED: 'clinicians_invite_dialog_opened',
  CLINICIANS_INVITE_SENT: 'clinicians_invite_sent',
  CLINICIANS_MEMBER_ROW_EXPANDED: 'clinicians_member_row_expanded',
  CLINICIANS_MEMBER_EDIT_OPENED: 'clinicians_member_edit_opened',
  CLINICIANS_MEMBER_ROLE_CHANGED: 'clinicians_member_role_changed',
  CLINICIANS_MEMBER_BILL_UNDER_CHANGED: 'clinicians_member_bill_under_changed',
  CLINICIANS_MEMBER_SUPERVISOR_CHANGED: 'clinicians_member_supervisor_changed',
  CLINICIANS_MEMBER_BUNDLE_CHANGED: 'clinicians_member_bundle_changed',
  CLINICIANS_MEMBER_DEACTIVATED: 'clinicians_member_deactivated',
  CLINICIANS_MEMBER_REACTIVATED: 'clinicians_member_reactivated',
  CLINICIANS_INVITATION_RESENT: 'clinicians_invitation_resent',
  CLINICIANS_INVITATION_DELETED: 'clinicians_invitation_deleted',
  CLINICIANS_INVITATION_EDITED: 'clinicians_invitation_edited',
  CLINICIANS_REFRESH_CLICKED: 'clinicians_refresh_clicked',

  // Data & Security bundle events (org-level purchase from /practice/data-security)
  DATA_SECURITY_ADD_TO_PLAN_CLICKED: 'data_security_add_to_plan_clicked',
  DATA_SECURITY_PURCHASE_CONFIRMED: 'data_security_purchase_confirmed',
  DATA_SECURITY_PURCHASE_SUCCEEDED: 'data_security_purchase_succeeded',
  DATA_SECURITY_PURCHASE_FAILED: 'data_security_purchase_failed',

  // Bundle learn more page
  BUNDLE_PAGE_VIEWED: 'bundle_page_viewed',
  BUNDLE_TAB_CHANGED: 'bundle_tab_changed',
  BUNDLE_TALK_TO_SALES_CLICKED: 'bundle_talk_to_sales_clicked',
  BUNDLE_BACK_CLICKED: 'bundle_back_clicked',

  // Patient deduplication events
  PATIENT_DEDUPLICATION_FIND_DUPLICATES_CLICKED: 'patient_deduplication_find_duplicates_clicked',
  PATIENT_DEDUPLICATION_SCAN_COMPLETED: 'patient_deduplication_scan_completed',
  PATIENT_DEDUPLICATION_SCAN_FAILED: 'patient_deduplication_scan_failed',
  PATIENT_DEDUPLICATION_MERGE_INITIATED: 'patient_deduplication_merge_initiated',
  PATIENT_DEDUPLICATION_MERGE_CUSTOMIZATION_VIEWED:
    'patient_deduplication_merge_customization_viewed',
  PATIENT_DEDUPLICATION_MERGE_COMPLETED: 'patient_deduplication_merge_completed',
  PATIENT_DEDUPLICATION_MERGE_FAILED: 'patient_deduplication_merge_failed',
  PATIENT_DEDUPLICATION_DISMISS_CLICKED: 'patient_deduplication_dismiss_clicked',
  PATIENT_DEDUPLICATION_DISMISS_COMPLETED: 'patient_deduplication_dismiss_completed',
  PATIENT_DEDUPLICATION_DISMISS_FAILED: 'patient_deduplication_dismiss_failed',
  PATIENT_DEDUPLICATION_AUTO_MERGE_CANDIDATES_LOADED:
    'patient_deduplication_auto_merge_candidates_loaded',
  PATIENT_DEDUPLICATION_BATCH_MERGE_COMPLETED: 'patient_deduplication_batch_merge_completed',
  PATIENT_DEDUPLICATION_BATCH_MERGE_FAILED: 'patient_deduplication_batch_merge_failed',

  // Patient import events
  PATIENT_IMPORT_FILE_SELECTED: 'patient_import_file_selected',
  PATIENT_IMPORT_UPLOAD_STARTED: 'patient_import_upload_started',
  PATIENT_IMPORT_UPLOAD_COMPLETED: 'patient_import_upload_completed',
  PATIENT_IMPORT_UPLOAD_FAILED: 'patient_import_upload_failed',
  PATIENT_IMPORT_SESSIONS_VIEWED: 'patient_import_sessions_viewed',
  PATIENT_IMPORT_SESSION_DELETED: 'patient_import_session_deleted',
  PATIENT_IMPORT_REVIEW_VIEWED: 'patient_import_review_viewed',
  PATIENT_IMPORT_APPROVED: 'patient_import_approved',
  PATIENT_IMPORT_COMPLETED: 'patient_import_completed',
  PATIENT_IMPORT_FAILED: 'patient_import_failed',
  PATIENT_IMPORT_CANCELLED: 'patient_import_cancelled',
  PATIENT_IMPORT_WIZARD_STEP_VIEWED: 'patient_import_wizard_step_viewed',
  PATIENT_IMPORT_WIZARD_DISMISSED: 'patient_import_wizard_dismissed',
  PATIENT_IMPORT_SKIP_APPOINTMENTS: 'patient_import_skip_appointments',
  PATIENT_IMPORT_SKIP_NEW_PATIENTS: 'patient_import_skip_new_patients',

  // Insurance card upload events
  INSURANCE_CARD_UPLOAD_STARTED: 'insurance_card_upload_started',
  INSURANCE_CARD_UPLOAD_COMPLETED: 'insurance_card_upload_completed',
  INSURANCE_CARD_UPLOAD_FAILED: 'insurance_card_upload_failed',

  // Coverage transitions (GDN-2411)
  INSURANCE_ADD_INCOMING_PLAN_CLICKED: 'insurance_add_incoming_plan_clicked',
  INSURANCE_HISTORY_OPENED: 'insurance_history_opened',
  COVERAGE_GAP_UPDATE_INSURANCE_CLICKED: 'coverage_gap_update_insurance_clicked',

  // System notification events
  SYSTEM_NOTIFICATION_BANNER_DISMISSED: 'system_notification_banner_dismissed',
  SYSTEM_NOTIFICATION_MODAL_OPENED: 'system_notification_modal_opened',
  SYSTEM_NOTIFICATION_MODAL_DISMISSED: 'system_notification_modal_dismissed',
  SYSTEM_NOTIFICATION_LINK_CLICKED: 'system_notification_link_clicked',
  SYSTEM_NOTIFICATION_FETCH_ERROR: 'system_notification_fetch_error',

  // Referral events
  REFERRAL_PAGE_VIEWED: 'referral_page_viewed',
  REFERRAL_LINK_COPIED: 'referral_link_copied',
  REFERRAL_LINK_COPY_FAILED: 'referral_link_copy_failed',
  REFERRAL_FAQ_EXPANDED: 'referral_faq_expanded',

  // JotRx onboarding events
  JOTRX_BANNER_SHOWN: 'jotrx_banner_shown',
  JOTRX_BANNER_DISMISSED: 'jotrx_banner_dismissed',
  JOTRX_SETUP_STARTED: 'jotrx_setup_started',
  JOTRX_SETUP_CALL_BOOKED: 'jotrx_setup_call_booked',
  JOTRX_DEMO_REQUESTED: 'jotrx_demo_requested',
  JOTRX_CALLOUT_SHOWN: 'jotrx_callout_shown',
  JOTRX_CALLOUT_CTA_CLICKED: 'jotrx_callout_cta_clicked',
  JOTRX_DOSESPOT_SETUP_OPENED: 'jotrx_dosespot_setup_opened',
  JOTRX_PRESCRIBE_OPENED: 'jotrx_prescribe_opened',
  JOTRX_ORG_SETUP_COMPLETED: 'jotrx_org_setup_completed',
  JOTRX_ORG_SETUP_FAILED: 'jotrx_org_setup_failed',
  JOTRX_CLINICIAN_REGISTERED: 'jotrx_clinician_registered',
  JOTRX_CLINICIAN_REGISTRATION_FAILED: 'jotrx_clinician_registration_failed',
  JOTRX_PATIENT_CONNECTED: 'jotrx_patient_connected',
  JOTRX_PATIENT_CONNECT_FAILED: 'jotrx_patient_connect_failed',
  PRESCRIBING_NOTIFICATIONS_BELL_OPENED: 'prescribing_notifications_bell_opened',
  PRESCRIBING_NOTIFICATIONS_ROW_CLICKED: 'prescribing_notifications_row_clicked',

  // EHR extension settings events
  EHR_EXTENSION_SETTINGS_VIEWED: 'ehr_extension_settings_viewed',
  EHR_EXTENSION_CHROME_STORE_CLICKED: 'ehr_extension_chrome_store_clicked',
  EHR_EXTENSION_ADD_TO_PLAN_CLICKED: 'ehr_extension_add_to_plan_clicked',
  EHR_EXTENSION_MANAGE_CLINICIANS_CLICKED: 'ehr_extension_manage_clinicians_clicked',
  EHR_EXTENSION_TUTORIAL_CLICKED: 'ehr_extension_tutorial_clicked',
  // Audio device service events
  AUDIO_DEVICE_SERVICE_INITIALIZED: 'audio_device_service_initialized',
  AUDIO_DEVICE_CHANGED: 'audio_device_changed',
  AUDIO_DEVICE_LOST: 'audio_device_lost',
  AUDIO_DEVICE_SUBSTITUTED: 'audio_device_substituted',
  AUDIO_HEALTH_STATE_CHANGED: 'audio_health_state_changed',
  AUDIO_PERMISSION_RESULT: 'audio_permission_result',
  AUDIO_PERFORMANCE_SUMMARY: 'audio_performance_summary',

  // Session completion & recovery events (ADR-007)
  SESSION_COMPLETION_DRAIN_COMPLETED: 'session_completion_drain_completed',
  SESSION_COMPLETION_FINISHED: 'session_completion_finished',
  SESSION_COMPLETION_TIMEOUT: 'session_completion_timeout',
  SESSION_COMPLETION_NETWORK_FAILURE: 'session_completion_network_failure',
  SESSION_COMPLETION_FATAL_ERROR: 'session_completion_fatal_error',
  SESSION_COMPLETION_PAGE_UNLOAD: 'session_completion_page_unload',
  SESSION_ABANDONED: 'session_abandoned',
  SESSION_RECOVERY_SCAN_COMPLETED: 'session_recovery_scan_completed',
  SESSION_RECOVERY_SESSION_RECOVERED: 'session_recovery_session_recovered',
  SESSION_RECOVERY_SESSION_FAILED: 'session_recovery_session_failed',
  SESSION_RECOVERY_STALE_MANIFEST_CLEANED: 'session_recovery_stale_manifest_cleaned',

  // Patient context bar events
  PATIENT_CONTEXT_BAR_DISMISSED: 'patient_context_bar_dismissed',
  PATIENT_CONTEXT_BAR_RESTORED: 'patient_context_bar_restored',
  PATIENT_CONTEXT_BAR_POPOVER_VIEWED: 'patient_context_bar_popover_viewed',
  PATIENT_CONTEXT_BAR_EDIT_MODAL_OPENED: 'patient_context_bar_edit_modal_opened',
  PATIENT_CONTEXT_BAR_EDIT_MODAL_CLOSED: 'patient_context_bar_edit_modal_closed',
  PATIENT_CONTEXT_BAR_VIEW_NOTE_CLICKED: 'patient_context_bar_view_note_clicked',
  PATIENT_CONTEXT_BAR_PATIENT_NAME_CLICKED: 'patient_context_bar_patient_name_clicked',
  PATIENT_CONTEXT_BAR_PATIENT_DESELECTED: 'patient_context_bar_patient_deselected',
  PATIENT_CONTEXT_BAR_SAVE_FAILED: 'patient_context_bar_save_failed',
  PATIENT_CONTEXT_BAR_EDIT_FROM_POPOVER_CLICKED: 'patient_context_bar_edit_from_popover_clicked',
  // Treatment plan modal events. Modal-specific namespace (not
  // context-bar-prefixed) because the modal opens from multiple surfaces —
  // context bar AND patient-page server-UI dispatches — and will eventually
  // live outside the patient-context-bar feature.
  PATIENT_TREATMENT_PLAN_OPENED: 'patient_treatment_plan_opened',
  PATIENT_TREATMENT_PLAN_CLOSED: 'patient_treatment_plan_closed',
  PATIENT_TREATMENT_PLAN_SAVE_FAILED: 'patient_treatment_plan_save_failed',
  PATIENT_TREATMENT_PLAN_IMPORT_CLICKED: 'patient_treatment_plan_import_clicked',
  PATIENT_TREATMENT_PLAN_IMPORT_COMPLETED: 'patient_treatment_plan_import_completed',
  PATIENT_TREATMENT_PLAN_IMPORT_FAILED: 'patient_treatment_plan_import_failed',

  // Telehealth events
  TELEHEALTH_JOIN_CLICKED: 'telehealth_join_clicked',
  TELEHEALTH_JOIN_ATTEMPTED: 'telehealth_join_attempted',
  TELEHEALTH_JOIN_SUCCEEDED: 'telehealth_join_succeeded',
  TELEHEALTH_JOIN_FAILED: 'telehealth_join_failed',
  TELEHEALTH_CALL_CONNECTED: 'telehealth_call_connected',
  TELEHEALTH_CALL_LEFT: 'telehealth_call_left',
  TELEHEALTH_CALL_ERROR: 'telehealth_call_error',
  TELEHEALTH_MIC_TOGGLED: 'telehealth_mic_toggled',
  TELEHEALTH_VIDEO_TOGGLED: 'telehealth_video_toggled',
  TELEHEALTH_PATIENT_JOINED: 'telehealth_patient_joined',
  TELEHEALTH_PATIENT_LEFT: 'telehealth_patient_left',
  TELEHEALTH_PARTICIPANT_ADMITTED: 'telehealth_participant_admitted',
  TELEHEALTH_PARTICIPANT_DENIED: 'telehealth_participant_denied',
  TELEHEALTH_WAITING_ROOM_REQUEST_SHOWN: 'telehealth_waiting_room_request_shown',
  TELEHEALTH_END_CALL_WITH_RECORDING_WARNING: 'telehealth_end_call_with_recording_warning',
  TELEHEALTH_SESSION_END_INITIATED: 'telehealth_session_end_initiated',
  TELEHEALTH_LINK_COPIED: 'telehealth_link_copied',
  TELEHEALTH_PATIENT_NOTIFIED: 'telehealth_patient_notified',
  TELEHEALTH_DEVICE_SWITCHED: 'telehealth_device_switched',
  TELEHEALTH_DEVICE_SWITCH_FAILED: 'telehealth_device_switch_failed',
  TELEHEALTH_PRECALL_COMPLETED: 'telehealth_precall_completed',
  TELEHEALTH_WAITING_ROOM_LINK_COPY_ATTEMPTED: 'telehealth_waiting_room_link_copy_attempted',
  TELEHEALTH_LINK_COPY_FAILED: 'telehealth_link_copy_failed',
  TELEHEALTH_SEND_TO_PATIENT_FAILED: 'telehealth_send_to_patient_failed',
  TELEHEALTH_SCREEN_SHARE_TOGGLED: 'telehealth_screen_share_toggled',
  TELEHEALTH_NOISE_CANCELLATION_TOGGLED: 'telehealth_noise_cancellation_toggled',
  TELEHEALTH_BACKGROUND_BLUR_CHANGED: 'telehealth_background_blur_changed',
  TELEHEALTH_SELF_VIEW_TOGGLED: 'telehealth_self_view_toggled',
  TELEHEALTH_LAYOUT_CHANGED: 'telehealth_layout_changed',
  TELEHEALTH_PIP_OPENED: 'telehealth_pip_opened',
  TELEHEALTH_PIP_CLOSED: 'telehealth_pip_closed',
  TELEHEALTH_PIP_ERROR: 'telehealth_pip_error',
  TELEHEALTH_PIP_AUTO_TRIGGERED: 'telehealth_pip_auto_triggered',
  TELEHEALTH_PREJOIN_COPY_LINK_CLICKED: 'telehealth_prejoin_copy_link_clicked',
  TELEHEALTH_PREJOIN_SEND_INVITE_CLICKED: 'telehealth_prejoin_send_invite_clicked',
  TELEHEALTH_WAITING_ROOM_PANEL_OPENED: 'telehealth_waiting_room_panel_opened',
  TELEHEALTH_WAITING_ROOM_PANEL_CLOSED: 'telehealth_waiting_room_panel_closed',
  TELEHEALTH_WAITING_ROOM_AVAILABILITY_CHANGED: 'telehealth_waiting_room_availability_changed',
  TELEHEALTH_WAITING_ROOM_REMOVE_REQUESTED: 'telehealth_waiting_room_remove_requested',
  TELEHEALTH_WAITING_ROOM_DESTINATION_PICKER_OPENED:
    'telehealth_waiting_room_destination_picker_opened',
  TELEHEALTH_WAITING_ROOM_DESTINATION_SELECTED: 'telehealth_waiting_room_destination_selected',
  TELEHEALTH_WAITING_ROOM_DESTINATION_AUTO_SELECTED:
    'telehealth_waiting_room_destination_auto_selected',
  TELEHEALTH_WAITING_ROOM_DESTINATION_CANCELLED: 'telehealth_waiting_room_destination_cancelled',
  TELEHEALTH_WAITING_ROOM_DESTINATION_LAUNCH_FAILED:
    'telehealth_waiting_room_destination_launch_failed',
  TELEHEALTH_WAITING_ROOM_AUTO_ADMIT_STARTED: 'telehealth_waiting_room_auto_admit_started',
  TELEHEALTH_WAITING_ROOM_AUTO_ADMIT_SUCCEEDED: 'telehealth_waiting_room_auto_admit_succeeded',
  TELEHEALTH_WAITING_ROOM_AUTO_ADMIT_FAILED: 'telehealth_waiting_room_auto_admit_failed',

  // Membership events
  MEMBERSHIP_DEACTIVATED_SCREEN_VIEWED: 'membership_deactivated_screen_viewed',

  // Tour events
  TOUR_PROMPT_SHOWN: 'tour_prompt_shown',
  TOUR_STARTED: 'tour_started',
  TOUR_STEP_VIEWED: 'tour_step_viewed',
  TOUR_DISMISSED: 'tour_dismissed',
  TOUR_COMPLETED: 'tour_completed',

  // Recording pipeline UI events
  RECORDING_HEALTH_DEGRADED: 'recording_health_degraded',
  UPLOAD_BANNER_SHOWN: 'upload_banner_shown',
  UPLOAD_BANNER_LOSS_ACKNOWLEDGED: 'upload_banner_loss_acknowledged',
  RECOVERY_BANNER_ACTION: 'recovery_banner_action',

  // Clinic document events
  CLINIC_DOC_UPLOADED: 'clinic_doc_uploaded',
  CLINIC_DOC_UPLOAD_FAILED: 'clinic_doc_upload_failed',
  CLINIC_DOC_DELETED: 'clinic_doc_deleted',
  CLINIC_DOC_TAB_SWITCHED: 'clinic_doc_tab_switched',
  CLINIC_DOC_FILE_VIEWED: 'clinic_doc_file_viewed',
  CLINIC_DOC_CHAT_MESSAGE_SENT: 'clinic_doc_chat_message_sent',
  CLINIC_DOC_CHAT_RESPONSE_RECEIVED: 'clinic_doc_chat_response_received',
  CLINIC_DOC_CHAT_ERROR: 'clinic_doc_chat_error',
  CLINIC_DOC_CHAT_CONVERSATION_CLEARED: 'clinic_doc_chat_conversation_cleared',
  CLINIC_DOC_CHAT_CITATION_CLICKED: 'clinic_doc_chat_citation_clicked',

  // Note Viewer
  NOTE_VIEWER_OPENED: 'note_viewer_opened',
  NOTE_VIEWER_OPEN_IN_NEW_TAB_CLICKED: 'note_viewer_open_in_new_tab_clicked',
  NOTE_VIEWER_PDF_DOWNLOADED: 'note_viewer_pdf_downloaded',
  NOTE_VIEWER_PDF_DOWNLOAD_FAILED: 'note_viewer_pdf_download_failed',

  // Addon upsell events
  ADDON_UPSELL_OPENED: 'addon_upsell_opened',
  ADDON_UPSELL_SLIDE_VIEWED: 'addon_upsell_slide_viewed',
  ADDON_UPSELL_SLIDE_NAVIGATED: 'addon_upsell_slide_navigated',
  ADDON_UPSELL_PURCHASE_VIEWED: 'addon_upsell_purchase_viewed',
  ADDON_UPSELL_PLAN_SELECTED: 'addon_upsell_plan_selected',
  ADDON_UPSELL_CLINICIAN_SELECTED: 'addon_upsell_clinician_selected',
  ADDON_UPSELL_CONFIRMED: 'addon_upsell_confirmed',
  ADDON_UPSELL_SUCCEEDED: 'addon_upsell_succeeded',
  ADDON_UPSELL_FAILED: 'addon_upsell_failed',
  ADDON_UPSELL_CLOSED: 'addon_upsell_closed',
  ADDON_UPSELL_ACCESS_REQUESTED: 'addon_upsell_access_requested',

  // Addon onboarding events (GDN-1709) — first-run onboarding for an added capability/role
  ADDON_ONBOARDING_OPENED: 'addon_onboarding_opened',
  ADDON_ONBOARDING_STEP_CHANGED: 'addon_onboarding_step_changed',
  ADDON_ONBOARDING_SUBMITTED: 'addon_onboarding_submitted',
  ADDON_ONBOARDING_CLOSED: 'addon_onboarding_closed',

  // JotMeet settings page (/user#telehealth)
  JOTMEET_SETTINGS_OPENED: 'jotmeet_settings_opened',
  JOTMEET_SETTINGS_LEARN_MORE_CLICKED: 'jotmeet_settings_learn_more_clicked',
  JOTMEET_SETTINGS_NON_ADMIN_LEARN_MORE_CLICKED: 'jotmeet_settings_non_admin_learn_more_clicked',
  JOTMEET_SETTINGS_LINK_COPY_ATTEMPTED: 'jotmeet_settings_link_copy_attempted',
  JOTMEET_SETTINGS_LINK_COPIED: 'jotmeet_settings_link_copied',

  // Clinical chart
  CLINICAL_CHART_EXPANDED: 'clinical_chart_expanded',
  CLINICAL_CHART_COLLAPSED: 'clinical_chart_collapsed',
  CLINICAL_CHART_ADD_MODAL_OPENED: 'clinical_chart_add_modal_opened',
  CLINICAL_CHART_ITEM_ADDED: 'clinical_chart_item_added',
  CLINICAL_CHART_ITEM_ADD_FAILED: 'clinical_chart_item_add_failed',
  CLINICAL_CHART_ITEM_UPDATED: 'clinical_chart_item_updated',
  CLINICAL_CHART_ITEM_UPDATE_FAILED: 'clinical_chart_item_update_failed',
  CLINICAL_CHART_ITEM_DELETED: 'clinical_chart_item_deleted',
  CLINICAL_CHART_ITEM_DELETE_FAILED: 'clinical_chart_item_delete_failed',
  CLINICAL_CHART_SUGGESTION_ACCEPTED: 'clinical_chart_suggestion_accepted',
  CLINICAL_CHART_SUGGESTION_DISMISSED: 'clinical_chart_suggestion_dismissed',

  // Server UI — Tile Row
  TILE_ACTION_CLICKED: 'tile_action_clicked',
  TILE_ACTION_FAILED: 'tile_action_failed',
  TILE_HOVERED: 'tile_hovered',

  // Thumbs Feedback (thumbs up/down)
  THUMBS_FEEDBACK_POSITIVE: 'thumbs_feedback_positive',
  THUMBS_FEEDBACK_NEGATIVE: 'thumbs_feedback_negative',
  THUMBS_FEEDBACK_CATEGORIES_SUBMITTED: 'thumbs_feedback_categories_submitted',
  THUMBS_FEEDBACK_DISMISSED: 'thumbs_feedback_dismissed',

  // Patient page redesign (v3) test invitation banner
  PATIENT_PAGE_V3_TEST_BANNER_OPENED: 'patient_page_v3_test_banner_opened',
  PATIENT_PAGE_V3_TEST_BANNER_CTA_CLICKED: 'patient_page_v3_test_banner_cta_clicked',
  PATIENT_PAGE_V3_TEST_BANNER_CLOSED: 'patient_page_v3_test_banner_closed',

  // Ghost assessment trajectory (server-driven empty-state preview on patient page).
  // No mount-based "viewed" event — fold-position makes mount unreliable as a
  // proxy for "user saw it". Hover dwell (>=2s) is the engagement signal.
  ASSESSMENT_TRAJECTORY_GHOST_HOVERED: 'assessment_trajectory_ghost_hovered',
  ASSESSMENT_TRAJECTORY_GHOST_CTA_CLICKED: 'assessment_trajectory_ghost_cta_clicked',
  ASSESSMENT_TRAJECTORY_GHOST_DISMISSED: 'assessment_trajectory_ghost_dismissed',

  // Medication history (SureScripts) on the patient page
  MEDICATION_HISTORY_OPENED: 'medication_history_opened',
  MEDICATION_HISTORY_CONSENT_CLICKED: 'medication_history_consent_clicked',
  MEDICATION_HISTORY_CONSENT_SUBMITTED: 'medication_history_consent_submitted',
  MEDICATION_HISTORY_SECTION_TOGGLED: 'medication_history_section_toggled',

  // Unified medications card on the V1 patient page (GDN-2351)
  UNIFIED_MEDS_CARD_OPENED: 'unified_meds_card_opened',
  UNIFIED_MEDS_FILTER_CHANGED: 'unified_meds_filter_changed',
  UNIFIED_MEDS_HISTORY_TOGGLED: 'unified_meds_history_toggled',
  UNIFIED_MEDS_SUGGESTIONS_TOGGLED: 'unified_meds_suggestions_toggled',
  UNIFIED_MEDS_ADD_CLICKED: 'unified_meds_add_clicked',
  UNIFIED_MEDS_EDIT_CLICKED: 'unified_meds_edit_clicked',
  UNIFIED_MEDS_DISCONTINUE_CLICKED: 'unified_meds_discontinue_clicked',
  UNIFIED_MEDS_REMOVE_CLICKED: 'unified_meds_remove_clicked',
  UNIFIED_MEDS_MANAGE_RX_CLICKED: 'unified_meds_manage_rx_clicked',

  // Redesigned diagnoses/allergies cards on the V1 patient page (GDN-2351).
  // One family with an item_type dimension rather than a constant per domain.
  CLINICAL_CARD_OPENED: 'clinical_card_opened',
  CLINICAL_CARD_FILTER_CHANGED: 'clinical_card_filter_changed',
  CLINICAL_CARD_ADD_CLICKED: 'clinical_card_add_clicked',
  CLINICAL_CARD_EDIT_CLICKED: 'clinical_card_edit_clicked',
  CLINICAL_CARD_STATUS_TOGGLED: 'clinical_card_status_toggled',
  CLINICAL_CARD_SUGGESTION_SELECTED: 'clinical_card_suggestion_selected',
  CLINICAL_CARD_SUGGESTIONS_TOGGLED: 'clinical_card_suggestions_toggled',

  // Appointment labels + groups (GDN-1968). Consolidated, Metabase-allowlisted
  // suffixes; the `action` metadata field carries the specific operation so the
  // funnels stay sliceable without a constant per CRUD verb.
  LABEL_CATALOG_CHANGED: 'label_catalog_changed',
  APPOINTMENT_LABELS_CHANGED: 'appointment_labels_changed',
  APPOINTMENT_TYPE_DEFAULT_LABELS_CHANGED: 'appointment_type_default_labels_changed',
  CALENDAR_GROUPED_BY_LABEL_SELECTED: 'calendar_grouped_by_label_selected',
  CALENDAR_LABEL_LANES_SPLIT_TOGGLED: 'calendar_label_lanes_split_toggled',
  LABELS_CATALOG_OPENED: 'labels_catalog_opened',

  // Self-serve scheduling settings (GDN-2187). Metabase-allowlisted suffixes;
  // the `action` metadata field carries the specific operation so funnels stay
  // sliceable without a constant per CRUD verb.
  SCHEDULING_SETTINGS_OPENED: 'scheduling_settings_opened',
  BOOKING_GATE_TOGGLED: 'booking_gate_toggled',
  BOOKING_GATE_CHANGED: 'booking_gate_changed',
  BOOKING_GATE_SETUP_PAYMENTS_CLICKED: 'booking_gate_setup_payments_clicked',
  AVAILABILITY_RULE_CHANGED: 'availability_rule_changed',
  // GDN-2272: patient self-serve manage policy (notice window + cancel toggle)
  BOOKING_MANAGE_NOTICE_CHANGED: 'booking_manage_notice_changed',
  BOOKING_SELF_CANCEL_TOGGLED: 'booking_self_cancel_toggled',
  // GDN-2222: confirmation modes + staff pending-bookings queue
  BOOKING_CONFIRMATION_MODE_CHANGED: 'booking_confirmation_mode_changed',
  BOOKING_REQUESTS_OPENED: 'booking_requests_opened',
  BOOKING_REQUEST_CONFIRM_CLICKED: 'booking_request_confirm_clicked',
  BOOKING_REQUEST_DECLINE_CLICKED: 'booking_request_decline_clicked',
  // GDN-2246: prospect identity review (promotion)
  BOOKING_REQUEST_REVIEW_OPENED: 'booking_request_review_opened',
  BOOKING_REQUEST_PROMOTE_SUBMITTED: 'booking_request_promote_submitted',
  // GDN-2414: copy-booking-link shortcut in the calendar header
  BOOKING_PAGE_LINK_COPIED: 'booking_page_link_copied',

  // Template ↔ appointment-type linking from the template side (GDN-2149).
  // Metabase-allowlisted suffixes; ids and counts only — never template
  // content or type names. `action` on _CHANGED distinguishes link vs undo.
  TEMPLATE_APPOINTMENT_TYPE_LINK_OPENED: 'template_appointment_type_link_opened',
  TEMPLATE_APPOINTMENT_TYPES_CHANGED: 'template_appointment_types_changed',
  TEMPLATE_CREATE_APPOINTMENT_TYPE_CLICKED: 'template_create_appointment_type_clicked',
} as const;

export type EventCategory = (typeof EventCategories)[keyof typeof EventCategories];
export type EventName = (typeof EventNames)[keyof typeof EventNames];
